import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { useParams, useHistory } from "react-router-dom";
import {
  Button,
  Box,
  Typography,
  FormControl,
  Select,
  Switch,
  MenuItem,
  Tooltip,
  IconButton,
  Alert,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Avatar,
  TextField,
  Tabs,
  Tab,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChatIcon from "@mui/icons-material/Chat";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import BusinessCenterIcon from "@mui/icons-material/BusinessCenter";
import StorefrontIcon from "@mui/icons-material/Storefront";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";
import LaunchIcon from "@mui/icons-material/Launch"; // open site icon
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import ShoppingCartOutlinedIcon from "@mui/icons-material/ShoppingCartOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";

import { getClientContacts, getCustomerDetails } from "../../../../services/customer";
import { convertAddressToString } from "../../../../utils/common";

import InstanceLeftSidebar from "../../InstanceLeftSidebar";
import PageHeaderWithBreadcrumbs from "../../../../components/PageHeaderWithBreadcrumbs";
import PresentationTabs from "../../../../components/PresentationTabs";
import InstanceProductManager from "../../connect/products/InstanceProducts"; // Import the popup component
import OrderUserAssignment from "../orders/orders-single/order-components/orderUserAssignment/OrderUserAssignment";
import OrderFiles from "../orders/orders-single/order-components/orderFiles/orderFiles";
import InstanceWebStoreDesign from "./design/InstanceWebStoreDesign";
import { buildPromoStoreBrandingSyncPayload } from "./design/promoStoreBrandingSync";
import InstanceWebStoreOrders from "./orders/InstanceWebStoreOrders";
import InstancePromoStoreUsers from "./users/InstancePromoStoreUsers";
import InstancePromoStoreReports from "./reports/InstancePromoStoreReports";
import PromoStoreCategoriesDialog from "./PromoStoreCategoriesDialog";
import {
  getPromoStorePaymentSettingsFromTemplateConfig,
  normalizePromoStorePaymentGatewayState,
} from "./promoStorePaymentGatewayState";
import { getPromoStoreSettingInputValue } from "./utils/promoStoreSettingText";
import "./promostore-overview.css";

import ChatBox from "./ChatBox";
import CustomerSearchSelectAsync from "../../../../components/CustomerSearchSelectAsync";

const PROMOSTORE_EVENT_TYPES = [
  "Advertising",
  "Awards",
  "Camp Clothing",
  "Celebration",
  "Client Appreciation",
  "Company Branding",
  "Company Offsite",
  "Conference",
  "Design",
  "Donor Gift",
  "Employee Appreciation",
  "Event Promotion",
  "Fundraiser",
  "Gift with Purchase",
  "Golf Tournament",
  "Holiday Event",
  "Individual",
  "Internal Use",
  "Mail Out",
  "On-premise",
  "Online Store",
  "Other",
  "PPE",
  "Product Launch",
  "Program",
  "Public Relations",
  "Recruitment",
  "Retail Merchandise",
  "Sales Tool",
  "Samples",
  "School Clothing",
  "School Orientation",
  "Seasonal Celebration",
  "Spirit Wear",
  "Sporting Event",
  "Team Building",
  "Thank-you Gift",
  "Trade Show",
  "Travel",
  "Uniforms",
  "Virtual",
  "Wedding",
  "Wellness Program",
];

/**
 * PERF FIXES SUMMARY (implemented below)
 * - Consolidate duplicate fetchStore/fetchOrder into one cancellable fetchPromostore()
 * - Remove broken `let isMounted = true` pattern
 * - Memoize CustomerSearchSelectAsync + stabilize onChange via useCallback
 * - Cache customer details (addresses) and cancel in-flight customer fetches
 * - Narrow address effect deps to selected customer id only
 * - Memoize fullStoreUrl
 */


const safeDisplayText = (...values) => {
  for (const value of values) {
    const text = String(value ?? "").trim();
    if (text) return text;
  }
  return "";
};

const normalizeCustomerLogoUrl = (value) => {
  const logo = safeDisplayText(value);
  if (!logo) return "";
  return logo.startsWith("uploads")
    ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${logo}`
    : logo;
};

const getCustomerLogoUrl = (customer = {}) => {
  const profilePicture = Array.isArray(customer?.profilePicture)
    ? customer.profilePicture.find((pic) => pic?.selected) || customer.profilePicture[0]
    : null;

  return normalizeCustomerLogoUrl(
    safeDisplayText(
      customer?.customUploadedBrandLogo,
      customer?.CustomUploadedBrandLogo,
      customer?.logoUrl,
      customer?.LogoUrl,
      customer?.logo,
      customer?.Logo,
      customer?.imageUrl,
      customer?.ImageUrl,
      customer?.avatarUrl,
      customer?.AvatarUrl,
      profilePicture?.url,
    ),
  );
};

const buildPromoStoreHeaderIdentity = (store = {}) => {
  const customer =
    store?.customers?.[0] ||
    store?.Customers?.[0] ||
    store?.Customer ||
    store?.customer ||
    {};
  const name = safeDisplayText(
    customer?.businessName,
    customer?.BusinessName,
    customer?.name,
    store?.CustomerName,
    store?.customerName,
    store?.ShopName,
    store?.title,
  );

  return {
    name,
    logoUrl: getCustomerLogoUrl(customer),
  };
};

const initialsFromName = (name = "") =>
  String(name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "S";

const PROMOSTORE_TABS = [
  { label: "Overview", Icon: DashboardOutlinedIcon },
  { label: "Files", Icon: FolderOutlinedIcon },
  { label: "Products", Icon: Inventory2OutlinedIcon },
  { label: "Design", Icon: PaletteOutlinedIcon },
  { label: "Orders", Icon: ReceiptLongOutlinedIcon },
  { label: "Reports", Icon: AssessmentOutlinedIcon },
  { label: "Users", Icon: PeopleAltOutlinedIcon },
];

// Memoize async customer select to avoid heavy rerenders/re-inits
const MemoCustomerSearchSelectAsync = React.memo(function MemoCustomerSearchSelectAsync(props) {
  return <CustomerSearchSelectAsync {...props} />;
});

// Simple in-memory cache for customer details (per session)
const customerDetailsCache = new Map();

function normalizeAssignedUserForPromoStore(user) {
  if (user == null) return null;
  const source = typeof user === "object" ? user : { id: user };
  const id = source.id ?? source.userId ?? source.user_id ?? source.value;
  if (id == null || id === "") return null;

  const fullName = [source.first_name, source.last_name].filter(Boolean).join(" ").trim();
  const fallbackName = fullName || source.name || source.email || `User #${id}`;

  return {
    ...source,
    id,
    role: source.role || "customer",
    first_name: source.first_name || fallbackName,
    last_name: source.last_name || "",
  };
}

function normalizeAssignedUsersForPromoStore(users) {
  if (!Array.isArray(users)) return [];
  return users.map(normalizeAssignedUserForPromoStore).filter(Boolean);
}

function getPromoStoreUserAvatar(user) {
  const defaultAvatarUrl = "/elite-core-placeholder-avatar.png";
  if (!user?.profilePicture) return defaultAvatarUrl;

  let profilePic = user.profilePicture;
  try {
    if (typeof profilePic === "string") profilePic = JSON.parse(profilePic);
  } catch (e) {
    profilePic = user.profilePicture;
  }

  if (Array.isArray(profilePic) && profilePic.length > 0) {
    const selectedPic = profilePic.find((pic) => pic.selected) || profilePic[0];
    if (selectedPic?.url) {
      return selectedPic.url.startsWith("uploads")
        ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${selectedPic.url}`
        : selectedPic.url;
    }
  }

  if (typeof profilePic === "string") {
    return profilePic.startsWith("uploads")
      ? `${process.env.REACT_APP_NODEJS_SERVER_URL}/${profilePic}`
      : profilePic;
  }

  return defaultAvatarUrl;
}

function getPromoStoreUserDisplayName(user, fallback = "Unknown creator") {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ").trim();
  return fullName || user?.name || user?.email || fallback;
}

function buildPromoStorePublicUrl(store) {
  const domain = String(store?.domain || "")
    .trim()
    .replace(/^https?:\/\//, "")
    .replace(/\/+$/, "");

  const path = String(store?.path || "/")
    .trim()
    .replace(/^\/+|\/+$/g, "");

  if (!domain) return "";

  return `https://${domain}/${path ? `${path}/` : ""}`;
}

const PROMOSTORE_SHIPPING_METHOD_OPTIONS = [
  { value: "free_shipping", label: "Free Shipping" },
  { value: "flat_rate", label: "Flat Rate" },
  { value: "local_pickup", label: "Local Pickup" },
];

const VALID_PROMOSTORE_SHIPPING_METHODS = new Set(
  PROMOSTORE_SHIPPING_METHOD_OPTIONS.map((option) => option.value),
);

const DEFAULT_SHIPPING_SETTINGS = {
  methods: ["free_shipping"],
  method: "free_shipping",
  flatRateAmount: "",
};

const DEFAULT_EMAIL_NOTIFICATIONS = {
  newOrder: { enabled: true, recipients: "" },
  failedOrder: { enabled: true, recipients: "" },
  cancelledOrder: { enabled: true, recipients: "" },
};

const PROMOSTORE_CHECKOUT_QUESTION_TYPES = new Set(["text", "textarea", "dropdown", "checkbox"]);
const PROMOSTORE_CHECKOUT_QUESTION_TYPE_LABELS = {
  text: "Text",
  textarea: "Text Area",
  dropdown: "Dropdown",
  checkbox: "Checkbox",
};
const EMPTY_CHECKOUT_QUESTION_DRAFT = {
  question: "",
  type: "text",
  mandatory: false,
  optionsText: "",
};

const DEFAULT_QUOTE_GATEWAY = {
  enabled: false,
  title: "Request Quote/Estimate",
  buttonText: "Submit Request",
  description: "Request a quote for your order.",
};
const DEFAULT_PURCHASE_ORDER = {
  enabled: false,
  questionLabel: "Do you have a P.O. number?",
  numberLabel: "P.O. Number",
  yesLabel: "Yes",
  noLabel: "No",
};

const PromoStoreInlineHelp = ({ label, title, stopPropagation = false }) => (
  <Tooltip title={title} arrow enterTouchDelay={0}>
    <IconButton
      type="button"
      size="small"
      className="promostore-inline-help"
      aria-label={label}
      onClick={stopPropagation ? (event) => event.stopPropagation() : undefined}
      onKeyDown={stopPropagation ? (event) => event.stopPropagation() : undefined}
    >
      <InfoOutlinedIcon fontSize="inherit" aria-hidden="true" />
    </IconButton>
  </Tooltip>
);

const handlePromoStoreHeaderKeyDown = (event, toggleExpanded) => {
  if (event.target !== event.currentTarget) return;
  if (event.key !== "Enter" && event.key !== " ") return;
  event.preventDefault();
  toggleExpanded();
};

const createPromoStoreCheckoutQuestionId = () => {
  if (typeof window !== "undefined" && typeof window.crypto?.randomUUID === "function") {
    return window.crypto.randomUUID();
  }
  return `checkout-question-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const normalizePromoStoreCheckoutQuestionOptions = (value) => {
  const seen = new Set();
  return (Array.isArray(value) ? value : [])
    .map((option) => String(option ?? "").trim())
    .filter((option) => {
      if (!option || seen.has(option)) return false;
      seen.add(option);
      return true;
    })
    .slice(0, 100)
    .map((option) => option.slice(0, 150));
};

const normalizePromoStoreCheckoutQuestions = (value) => {
  if (!Array.isArray(value)) return [];
  const seenIds = new Set();
  return value.slice(0, 50).reduce((questions, record) => {
    if (!record || typeof record !== "object" || Array.isArray(record)) return questions;
    const question = String(record.question ?? "").trim().slice(0, 255);
    if (!question) return questions;
    const requestedType = String(record.type ?? "text").trim().toLowerCase();
    const type = PROMOSTORE_CHECKOUT_QUESTION_TYPES.has(requestedType) ? requestedType : "text";
    let id = String(record.id ?? "").trim();
    if (!id || seenIds.has(id)) id = createPromoStoreCheckoutQuestionId();
    seenIds.add(id);
    questions.push({
      id,
      question,
      type,
      mandatory: record.mandatory === true,
      options: type === "dropdown" ? normalizePromoStoreCheckoutQuestionOptions(record.options) : [],
      sortOrder: questions.length,
    });
    return questions;
  }, []);
};

const getPromoStoreCheckoutQuestions = (store = {}) => (
  normalizePromoStoreCheckoutQuestions(getPromoStoreTemplateConfig(store).checkoutQuestions)
);

function getPromoStoreTemplateConfig(store) {
  return store?.TemplateConfig && typeof store.TemplateConfig === "object" ? store.TemplateConfig : {};
}

function getPromoStoreQuotePurchaseOrderSettings(store) {
  const config = getPromoStoreTemplateConfig(store);
  const quoteGateway = config.quoteGateway && typeof config.quoteGateway === "object" ? config.quoteGateway : {};
  const purchaseOrder = config.purchaseOrder && typeof config.purchaseOrder === "object" ? config.purchaseOrder : {};
  return {
    quoteGateway: {
      enabled: quoteGateway.enabled === true,
      title: getPromoStoreSettingInputValue(quoteGateway.title, DEFAULT_QUOTE_GATEWAY.title),
      buttonText: getPromoStoreSettingInputValue(quoteGateway.buttonText, DEFAULT_QUOTE_GATEWAY.buttonText),
      description: getPromoStoreSettingInputValue(quoteGateway.description, DEFAULT_QUOTE_GATEWAY.description),
    },
    purchaseOrder: {
      enabled: purchaseOrder.enabled === true,
      questionLabel: getPromoStoreSettingInputValue(purchaseOrder.questionLabel, DEFAULT_PURCHASE_ORDER.questionLabel),
      numberLabel: getPromoStoreSettingInputValue(purchaseOrder.numberLabel, DEFAULT_PURCHASE_ORDER.numberLabel),
      yesLabel: getPromoStoreSettingInputValue(purchaseOrder.yesLabel, DEFAULT_PURCHASE_ORDER.yesLabel),
      noLabel: getPromoStoreSettingInputValue(purchaseOrder.noLabel, DEFAULT_PURCHASE_ORDER.noLabel),
    },
  };
}

const normalizePromoStoreShippingMethods = (methods = [], fallbackMethod = "") => {
  const requested = Array.isArray(methods)
    ? methods
    : fallbackMethod
      ? [fallbackMethod]
      : [];
  const normalizedRequested = requested
    .map((method) => String(method || "").trim())
    .filter((method) => VALID_PROMOSTORE_SHIPPING_METHODS.has(method));
  const uniqueRequested = new Set(normalizedRequested);
  const ordered = PROMOSTORE_SHIPPING_METHOD_OPTIONS
    .map((option) => option.value)
    .filter((method) => uniqueRequested.has(method));

  return ordered.length ? ordered : [...DEFAULT_SHIPPING_SETTINGS.methods];
};

function getPromoStoreShippingSettings(store) {
  const cfg = getPromoStoreTemplateConfig(store);
  const shipping = cfg.shipping && typeof cfg.shipping === "object" ? cfg.shipping : {};
  const methods = Array.isArray(shipping.methods)
    ? normalizePromoStoreShippingMethods(shipping.methods, shipping.method)
    : normalizePromoStoreShippingMethods([], shipping.method);
  const flatRateAmount = shipping.flatRateAmount !== undefined && shipping.flatRateAmount !== null
    ? String(shipping.flatRateAmount)
    : methods.includes("flat_rate")
      ? "0"
      : DEFAULT_SHIPPING_SETTINGS.flatRateAmount;

  return {
    ...DEFAULT_SHIPPING_SETTINGS,
    ...shipping,
    methods,
    method: methods[0] || DEFAULT_SHIPPING_SETTINGS.method,
    flatRateAmount,
  };
}

function getPromoStorePaymentSettings(store) {
  return getPromoStorePaymentSettingsFromTemplateConfig(
    getPromoStoreTemplateConfig(store),
  );
}

function getPromoStoreEmailNotifications(store) {
  const cfg = getPromoStoreTemplateConfig(store);
  const notifications =
    cfg.emailNotifications && typeof cfg.emailNotifications === "object"
      ? cfg.emailNotifications
      : {};

  return Object.keys(DEFAULT_EMAIL_NOTIFICATIONS).reduce((acc, key) => {
    acc[key] = {
      ...DEFAULT_EMAIL_NOTIFICATIONS[key],
      ...(notifications[key] || {}),
      enabled:
        notifications[key]?.enabled === undefined
          ? DEFAULT_EMAIL_NOTIFICATIONS[key].enabled
          : !!notifications[key].enabled,
      recipients: notifications[key]?.recipients || "",
    };
    return acc;
  }, {});
}


const PROMOSTORE_CHECKOUT_ADDRESS_MODES = new Set(["open", "approved_only", "approved_or_new"]);
const PROMOSTORE_CHECKOUT_MODE_SAVE_KEY_PAIRS = [
  ["checkoutAddressMode", "saveNewCustomerAddresses"],
  ["billingAddressMode", "saveNewBillingAddresses"],
  ["billingContactMode", "saveNewBillingContacts"],
  ["shippingContactMode", "saveNewShippingContacts"],
];

const createPromoStoreCheckoutAddressDefaults = () => ({
  checkoutAddressMode: "open",
  approvedShippingAddressIds: [],
  approvedShippingAddressSnapshots: [],
  saveNewCustomerAddresses: false,
  billingAddressMode: "open",
  approvedBillingAddressIds: [],
  approvedBillingAddressSnapshots: [],
  saveNewBillingAddresses: false,
  billingContactMode: "open",
  approvedBillingContactIds: [],
  approvedBillingContactSnapshots: [],
  saveNewBillingContacts: false,
  shippingContactMode: "open",
  approvedShippingContactIds: [],
  approvedShippingContactSnapshots: [],
  saveNewShippingContacts: false,
  billingContactId: "",
  billingContactSnapshot: null,
  shippingContactId: "",
  shippingContactSnapshot: null,
  billingAddressId: "",
  billingAddressSnapshot: null,
});

const normalizePromoStoreCheckoutAddressMode = (value) => (
  PROMOSTORE_CHECKOUT_ADDRESS_MODES.has(value) ? value : "open"
);

const normalizePromoStoreId = (value) => (value === undefined || value === null ? "" : String(value).trim());

const normalizePromoStoreSnapshot = (value) => (
  value && typeof value === "object" && !Array.isArray(value) ? value : null
);

const normalizePromoStoreApprovedSnapshots = (value) => (
  Array.isArray(value) ? value.filter((snapshot) => snapshot && typeof snapshot === "object") : []
);

const normalizePromoStoreSelectionIds = (value, fallback = []) => {
  const seen = new Set();
  return [
    ...(Array.isArray(value) ? value : []),
    ...fallback,
  ]
    .map((item) => normalizePromoStoreId(item))
    .filter((itemId) => {
      if (!itemId || seen.has(itemId)) return false;
      seen.add(itemId);
      return true;
    });
};

const filterPromoStoreSelectionIds = (value, validIds = new Set()) => (
  normalizePromoStoreSelectionIds(value).filter((itemId) => validIds.has(itemId))
);

const getPromoStoreCheckoutAddressSettings = (store = {}) => {
  const cfg = getPromoStoreTemplateConfig(store);
  const checkoutAddresses = cfg.checkoutAddresses && typeof cfg.checkoutAddresses === "object" ? cfg.checkoutAddresses : {};
  const defaults = createPromoStoreCheckoutAddressDefaults();
  const billingContactId = normalizePromoStoreId(checkoutAddresses.billingContactId);
  const shippingContactId = normalizePromoStoreId(checkoutAddresses.shippingContactId);
  const billingAddressId = normalizePromoStoreId(checkoutAddresses.billingAddressId);

  return {
    ...defaults,
    checkoutAddressMode: normalizePromoStoreCheckoutAddressMode(checkoutAddresses.checkoutAddressMode),
    approvedShippingAddressIds: normalizePromoStoreSelectionIds(checkoutAddresses.approvedShippingAddressIds),
    approvedShippingAddressSnapshots: normalizePromoStoreApprovedSnapshots(checkoutAddresses.approvedShippingAddressSnapshots),
    saveNewCustomerAddresses: checkoutAddresses.saveNewCustomerAddresses === true,
    billingAddressMode: normalizePromoStoreCheckoutAddressMode(checkoutAddresses.billingAddressMode),
    approvedBillingAddressIds: normalizePromoStoreSelectionIds(
      checkoutAddresses.approvedBillingAddressIds,
      billingAddressId ? [billingAddressId] : []
    ),
    approvedBillingAddressSnapshots: normalizePromoStoreApprovedSnapshots(checkoutAddresses.approvedBillingAddressSnapshots),
    saveNewBillingAddresses: checkoutAddresses.saveNewBillingAddresses === true,
    billingContactMode: normalizePromoStoreCheckoutAddressMode(checkoutAddresses.billingContactMode),
    approvedBillingContactIds: normalizePromoStoreSelectionIds(
      checkoutAddresses.approvedBillingContactIds,
      billingContactId ? [billingContactId] : []
    ),
    approvedBillingContactSnapshots: normalizePromoStoreApprovedSnapshots(checkoutAddresses.approvedBillingContactSnapshots),
    saveNewBillingContacts: checkoutAddresses.saveNewBillingContacts === true,
    shippingContactMode: normalizePromoStoreCheckoutAddressMode(checkoutAddresses.shippingContactMode),
    approvedShippingContactIds: normalizePromoStoreSelectionIds(
      checkoutAddresses.approvedShippingContactIds,
      shippingContactId ? [shippingContactId] : []
    ),
    approvedShippingContactSnapshots: normalizePromoStoreApprovedSnapshots(checkoutAddresses.approvedShippingContactSnapshots),
    saveNewShippingContacts: checkoutAddresses.saveNewShippingContacts === true,
    billingContactId,
    billingContactSnapshot: normalizePromoStoreSnapshot(checkoutAddresses.billingContactSnapshot),
    shippingContactId,
    shippingContactSnapshot: normalizePromoStoreSnapshot(checkoutAddresses.shippingContactSnapshot),
    billingAddressId,
    billingAddressSnapshot: normalizePromoStoreSnapshot(checkoutAddresses.billingAddressSnapshot),
  };
};

const cleanContactPart = (value) => String(value ?? "").trim();

const getPromoStoreContactId = (contact = {}) => (
  normalizePromoStoreId(contact.contactId || contact.id || contact.email)
);

const formatPromoStoreContactLabel = (contact = {}) => {
  const fullName = [contact.firstName, contact.lastName].map(cleanContactPart).filter(Boolean).join(" ");
  return fullName || cleanContactPart(contact.name) || cleanContactPart(contact.email) || "Unnamed contact";
};

const formatPromoStoreContactSnapshot = (contact = {}) => ({
  id: getPromoStoreContactId(contact),
  contactId: contact.contactId || contact.id || null,
  name: formatPromoStoreContactLabel(contact),
  firstName: contact.firstName || null,
  lastName: contact.lastName || null,
  email: contact.email || null,
  phone: contact.phone || null,
});

const formatPromoStoreClientContactOptions = (contacts = []) => (
  (Array.isArray(contacts) ? contacts : [])
    .map((contact) => ({
      id: getPromoStoreContactId(contact),
      label: formatPromoStoreContactLabel(contact),
      contact,
    }))
    .filter((contact) => contact.id)
);

const getPromoStoreContactSnapshotDisplay = (snapshot = null) => {
  if (!snapshot || typeof snapshot !== "object") return "";
  const fullName = [snapshot.firstName, snapshot.lastName]
    .map(cleanContactPart)
    .filter(Boolean)
    .join(" ");
  return fullName || cleanContactPart(snapshot.name) || cleanContactPart(snapshot.email);
};

const parsePromoStoreMaybeJsonObject = (value) => {
  const raw = String(value || "").trim();
  if (!raw.startsWith("{") || !raw.endsWith("}")) return null;
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const resolvePromoStoreSelectedContactId = (contacts = [], savedId = null, savedSnapshot = null) => {
  const raw = normalizePromoStoreId(savedId);
  if (raw && contacts.some((contact) => contact.id === raw)) {
    return raw;
  }

  const savedObject = parsePromoStoreMaybeJsonObject(savedId);
  const snapshot = normalizePromoStoreSnapshot(savedSnapshot);
  const idCandidates = [
    savedObject?.contactId,
    savedObject?.id,
    savedObject?.email,
    snapshot?.contactId,
    snapshot?.id,
    snapshot?.email,
  ]
    .map((value) => normalizePromoStoreId(value))
    .filter(Boolean);

  const matchedId = idCandidates.find((candidate) => contacts.some((contact) => contact.id === candidate));
  if (matchedId) return matchedId;

  const displayCandidates = [
    getPromoStoreContactSnapshotDisplay(snapshot),
    [savedObject?.firstName, savedObject?.lastName].map(cleanContactPart).filter(Boolean).join(" "),
    cleanContactPart(savedObject?.name),
    cleanContactPart(savedObject?.email),
    raw,
  ]
    .map((value) => String(value || "").trim().toLowerCase())
    .filter(Boolean);

  if (displayCandidates.length) {
    const matchedContact = contacts.find((option) => {
      const optionContact = option?.contact || {};
      const optionName = [optionContact.firstName, optionContact.lastName]
        .map(cleanContactPart)
        .filter(Boolean)
        .join(" ");
      const haystacks = [
        option.label,
        optionContact.email,
        optionContact.name,
        optionName,
      ]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean);

      return displayCandidates.some((candidate) => haystacks.includes(candidate));
    });

    if (matchedContact?.id) {
      return String(matchedContact.id);
    }
  }

  const legacyMatch = raw.match(/^cus-(\d+)$/);
  if (legacyMatch) {
    const legacyOption = contacts[Number(legacyMatch[1])];
    if (legacyOption?.id) return String(legacyOption.id);
  }

  return "";
};

const findPromoStoreClientContact = (contacts = [], contactId = null) => (
  contacts.find((contact) => contact.id === normalizePromoStoreId(contactId)) || null
);

const cleanAddressPart = (value) => String(value ?? "").trim();

const formatPromoStoreAddressLine = (addr = {}) => [
  addr.streetAddress || addr.street || addr.address1,
  addr.city,
  addr.stateProvince || addr.state || addr.province,
  addr.postalZipCode || addr.postal || addr.zip,
  addr.country,
].map(cleanAddressPart).filter(Boolean).join(", ");

const formatCustomerAddressLabel = (addr = {}) => {
  const addressName = cleanAddressPart(addr.addressName || addr.name);
  const physicalAddress = formatPromoStoreAddressLine(addr);

  if (addressName && physicalAddress) return `${addressName} - ${physicalAddress}`;
  return addressName || physicalAddress || "Unnamed address";
};

const getPromoStoreCustomerAddressId = (addr = {}) => {
  const rawId = addr.id ?? addr.AddressId ?? addr.addressId;
  return rawId === undefined || rawId === null || rawId === "" ? "" : String(rawId);
};

const formatPromoStoreApprovedAddressSnapshot = (addr = {}) => ({
  id: getPromoStoreCustomerAddressId(addr) || convertAddressToString(addr),
  name: addr.addressName || addr.name || "Address",
  companyName: addr.companyName || null,
  streetAddress: addr.streetAddress || addr.street || null,
  suiteApt: addr.suiteApt || null,
  city: addr.city || null,
  stateProvince: addr.stateProvince || addr.state || null,
  postalZipCode: addr.postalZipCode || addr.postal || null,
  country: addr.country || null,
});

const buildPromoStoreApprovedAddressSnapshots = (addresses = [], addressIds = []) => {
  const addressMap = new Map(
    addresses
      .map((addr) => [normalizePromoStoreId(addr.approvedId || addr.id), addr.address])
      .filter(([addressId, address]) => Boolean(addressId) && address)
  );

  return normalizePromoStoreSelectionIds(addressIds)
    .map((addressId) => addressMap.get(addressId))
    .filter(Boolean)
    .map((address) => formatPromoStoreApprovedAddressSnapshot(address));
};

const buildPromoStoreApprovedContactSnapshots = (contacts = [], contactIds = []) => {
  const contactMap = new Map(
    contacts
      .map((contact) => [normalizePromoStoreId(contact.id), contact.contact])
      .filter(([contactId, rawContact]) => Boolean(contactId) && rawContact)
  );

  return normalizePromoStoreSelectionIds(contactIds)
    .map((contactId) => contactMap.get(contactId))
    .filter(Boolean)
    .map((contact) => formatPromoStoreContactSnapshot(contact));
};

const findPromoStoreClientAddress = (addresses = [], addressId = null) => (
  addresses.find((addr) => addr.id === String(addressId || "") || addr.legacyId === addressId || convertAddressToString(addr.address) === addressId)
);

const resolvePromoStoreSelectedAddressId = (addresses = [], savedAddress = null) => {
  if (!savedAddress) return null;
  const stableId = getPromoStoreCustomerAddressId(savedAddress);
  if (stableId) return stableId;

  const legacyId = convertAddressToString(savedAddress);
  const matched = addresses.find((addr) => addr.legacyId === legacyId);
  return matched?.id || legacyId || null;
};

const reconcilePromoStoreCheckoutAddressSettings = (settings, contacts = [], addresses = []) => {
  const current = {
    ...createPromoStoreCheckoutAddressDefaults(),
    ...settings,
  };
  const validContactIds = new Set(
    contacts.map((contact) => normalizePromoStoreId(contact.id)).filter(Boolean)
  );
  const validAddressIds = new Set(
    addresses.map((addr) => normalizePromoStoreId(addr.approvedId || addr.id)).filter(Boolean)
  );
  const billingContactId = resolvePromoStoreSelectedContactId(
    contacts,
    current.billingContactId,
    current.billingContactSnapshot
  );
  const shippingContactId = resolvePromoStoreSelectedContactId(
    contacts,
    current.shippingContactId,
    current.shippingContactSnapshot
  );
  const billingAddressId = validAddressIds.has(current.billingAddressId) ? current.billingAddressId : "";
  const approvedShippingAddressIds = filterPromoStoreSelectionIds(current.approvedShippingAddressIds, validAddressIds);
  const approvedBillingAddressIds = filterPromoStoreSelectionIds(current.approvedBillingAddressIds, validAddressIds);
  const approvedBillingContactIds = filterPromoStoreSelectionIds(current.approvedBillingContactIds, validContactIds);
  const approvedShippingContactIds = filterPromoStoreSelectionIds(current.approvedShippingContactIds, validContactIds);
  const billingContact = contacts.find((contact) => contact.id === billingContactId)?.contact || null;
  const shippingContact = contacts.find((contact) => contact.id === shippingContactId)?.contact || null;
  const billingAddress = addresses.find((addr) => (addr.approvedId || addr.id) === billingAddressId)?.address || null;

  return {
    ...current,
    approvedShippingAddressIds,
    approvedShippingAddressSnapshots: buildPromoStoreApprovedAddressSnapshots(addresses, approvedShippingAddressIds),
    approvedBillingAddressIds,
    approvedBillingAddressSnapshots: buildPromoStoreApprovedAddressSnapshots(addresses, approvedBillingAddressIds),
    approvedBillingContactIds,
    approvedBillingContactSnapshots: buildPromoStoreApprovedContactSnapshots(contacts, approvedBillingContactIds),
    approvedShippingContactIds,
    approvedShippingContactSnapshots: buildPromoStoreApprovedContactSnapshots(contacts, approvedShippingContactIds),
    billingContactId,
    billingContactSnapshot: billingContact ? formatPromoStoreContactSnapshot(billingContact) : null,
    shippingContactId,
    shippingContactSnapshot: shippingContact ? formatPromoStoreContactSnapshot(shippingContact) : null,
    billingAddressId,
    billingAddressSnapshot: billingAddress ? formatPromoStoreApprovedAddressSnapshot(billingAddress) : null,
  };
};

const arePromoStoreCheckoutAddressSettingsEqual = (left, right) => (
  JSON.stringify(left) === JSON.stringify(right)
);

function mergePromoStoreTemplateConfig(store, partial) {
  return normalizePromoStorePaymentGatewayState({
    ...getPromoStoreTemplateConfig(store),
    ...partial,
  });
}

const getTaxPendingRegionKey = (region) => (
  region === "united_states" ? "unitedStates" : "canada"
);

const getPromoStorePendingTaxConfiguration = (store) => {
  const pending = getPromoStoreTemplateConfig(store)?.taxConfiguration?.pending;
  return pending && typeof pending === "object" ? pending : {};
};

const normalizePendingTaxEntry = (entry, region) => {
  if (!entry || typeof entry !== "object") return null;
  const csv = typeof entry.csv === "string" ? entry.csv : "";
  if (!csv.trim()) return null;
  return {
    region,
    filename: typeof entry.filename === "string" ? entry.filename : "",
    rowCount: Number(entry.rowCount || 0) || 0,
    fileHash: typeof entry.fileHash === "string" ? entry.fileHash : "",
    size: Number(entry.size || 0) || 0,
    valid: entry.valid !== false,
    csv,
    detectedStates: Array.isArray(entry.detectedStates) ? entry.detectedStates : [],
    duplicateCount: Number(entry.duplicateCount || 0) || 0,
    alreadyActive: entry.alreadyActive === true,
    warnings: Array.isArray(entry.warnings) ? entry.warnings : [],
    errors: Array.isArray(entry.errors) ? entry.errors : [],
  };
};

const buildPendingTaxValidation = (entry, region) => {
  const normalized = normalizePendingTaxEntry(entry, region);
  if (!normalized) return null;
  return {
    valid: normalized.valid,
    region,
    filename: normalized.filename,
    rowCount: normalized.rowCount,
    fileHash: normalized.fileHash,
    size: normalized.size,
    detectedStates: normalized.detectedStates,
    duplicateCount: normalized.duplicateCount,
    alreadyActive: normalized.alreadyActive,
    warnings: normalized.warnings,
    errors: normalized.errors,
    persisted: true,
  };
};

const getCurrentUserRole = () => {
  try {
    const token = localStorage.getItem("accessToken");
    if (!token) return "";
    const payloadSegment = token.split(".")[1];
    if (!payloadSegment) return "";
    const normalized = payloadSegment.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(normalized)
        .split("")
        .map((char) => `%${(`00${char.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join("")
    );
    const payload = JSON.parse(json);
    return String(payload?.role || "").toLowerCase();
  } catch (_error) {
    return "";
  }
};

const DEFAULT_PROMOSTORE_STORE_TYPE = "company_store";
const PROMOSTORE_STORE_TYPE_VALUES = new Set(["company_store", "pop_up_store", "program_store"]);

const normalizePromoStoreStoreType = (value) => {
  const normalized = String(value || "").trim();
  const legacyMap = {
    "company-store": "company_store",
    "pop-up-store": "pop_up_store",
    program: "program_store",
  };
  const storeType = legacyMap[normalized] || normalized;

  return PROMOSTORE_STORE_TYPE_VALUES.has(storeType) ? storeType : DEFAULT_PROMOSTORE_STORE_TYPE;
};

const resolveAttachedCustomerId = (store) => {
  if (Array.isArray(store?.attachedCustomers) && store.attachedCustomers[0]) return store.attachedCustomers[0];
  if (Array.isArray(store?.AttachedCustomers) && store.AttachedCustomers[0]) return store.AttachedCustomers[0];
  if (Array.isArray(store?.AttachedCustomerIds) && store.AttachedCustomerIds[0]) return store.AttachedCustomerIds[0];

  return (
    store?.AttachedCustomerId ||
    store?.attachedCustomerId ||
    store?.customerId ||
    store?.CustomerId ||
    store?.customer?.id ||
    store?.Customer?.id ||
    null
  );
};

const normalizePromoStoreForFiles = (store) => {
  if (!store) return store;
  const attachedCustomerId = resolveAttachedCustomerId(store);

  return {
    ...store,
    attachedCustomers: attachedCustomerId ? [attachedCustomerId] : [],
  };
};

const normalizePromoStoreRecord = (promostore) => {
  if (!promostore) return promostore;
  return normalizePromoStoreForFiles({
    ...promostore,
    StoreType: normalizePromoStoreStoreType(promostore.StoreType),
  });
};

const InstancePromoStoreDetails = () => {
  const { id, instanceUniqueId, tabtype } = useParams(); // id of the web store
  const history = useHistory();

  const [store, setStore] = useState({
    status: "live",
    StoreType: DEFAULT_PROMOSTORE_STORE_TYPE,
    AssignedTo: [],
  });

  const [loading, setLoading] = useState(true);
  const [showActions, setShowActions] = useState(false);
  const [activeTab, setActiveTab] = useState("Overview");
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [order, setOrder] = useState({});
  const [promoStoreCategories, setPromoStoreCategories] = useState([]);
  const [assignedUsers, setAssignedUsers] = useState([]);
  const [showProductManagerPopup, setShowProductManagerPopup] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncDialogOpen, setSyncDialogOpen] = useState(false);
  const [syncDirection, setSyncDirection] = useState("core_to_wordpress");
  const [availableUsers, setAvailableUsers] = useState([]);

  // all the addresses for the currently selected customer
  const [clientAddresses, setClientAddresses] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [storeAddressId, setStoreAddressId] = useState(null);

  const [expanded, setExpanded] = useState(false);
  const [requestingDev, setRequestingDev] = useState(false); // request developer state
  const [requestDeveloperOpen, setRequestDeveloperOpen] = useState(false);
  const [promoStoreSettings, setPromoStoreSettings] = useState({
    requestDeveloperLabel: "Request developer",
    developerTeamUserIds: [],
  });
  const [developerTeamUsers, setDeveloperTeamUsers] = useState([]);
  const currentUserRole = useMemo(() => getCurrentUserRole(), []);
  const canRunWordpressToCoreSync = currentUserRole === "administrator";
  const isAdministrator = currentUserRole === "administrator";
  const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
  const [archiveConfirmText, setArchiveConfirmText] = useState("");
  // TODO: Persist this when the backend/API adds a compatible PromoStore tax-region field.
  const [taxRegionDraft, setTaxRegionDraft] = useState("canada");
  const [taxCsvFiles, setTaxCsvFiles] = useState({ canada: null, united_states: null });
  const [taxCsvValidation, setTaxCsvValidation] = useState({ canada: null, united_states: null });
  const [taxCsvWorking, setTaxCsvWorking] = useState({ canada: false, united_states: false, applying: false });
  const [taxCsvStatus, setTaxCsvStatus] = useState(null);
  const [taxRestoreWorking, setTaxRestoreWorking] = useState(false);
  const [taxRestoreConfirmOpen, setTaxRestoreConfirmOpen] = useState(false);
  const [taxExpandedErrors, setTaxExpandedErrors] = useState({});
  const [taxExpandedRegions, setTaxExpandedRegions] = useState({});
  const [taxConfigurationExpanded, setTaxConfigurationExpanded] = useState(true);
  const [expandedTaxCountry, setExpandedTaxCountry] = useState("canada");
  const [taxSuccessDetailsExpanded, setTaxSuccessDetailsExpanded] = useState(false);
  const [taxHistory, setTaxHistory] = useState({ items: [], pagination: { page: 1, pageSize: 20, total: 0, totalPages: 0 } });
  const [taxHistoryError, setTaxHistoryError] = useState("");
  const [taxApplyConfirmOpen, setTaxApplyConfirmOpen] = useState(false);
  const [archiveWorking, setArchiveWorking] = useState(false);
  const [archiveMessage, setArchiveMessage] = useState(null);
  const [approvedListDialogState, setApprovedListDialogState] = useState(null);
  const [approvedListDraftIds, setApprovedListDraftIds] = useState([]);
  const [approvedListCandidateId, setApprovedListCandidateId] = useState("");
  const [contactManagementDialogState, setContactManagementDialogState] = useState(null);
  const [contactManagementDraftIds, setContactManagementDraftIds] = useState([]);
  const [contactManagementCandidateId, setContactManagementCandidateId] = useState("");
  const [checkoutExpanded, setCheckoutExpanded] = useState(true);
  const [quotePurchaseOrderExpanded, setQuotePurchaseOrderExpanded] = useState(true);
  const [checkoutQuestionDraft, setCheckoutQuestionDraft] = useState(EMPTY_CHECKOUT_QUESTION_DRAFT);
  const [editingCheckoutQuestionId, setEditingCheckoutQuestionId] = useState("");
  const [checkoutQuestionError, setCheckoutQuestionError] = useState("");

  // product popup / manager state
  const [currentProduct, setCurrentProduct] = useState(null);
  const [popupInitialView, setPopupInitialView] = useState("addprod");
  const [categoriesDialogOpen, setCategoriesDialogOpen] = useState(false);
  const [categorySaving, setCategorySaving] = useState(false);

  // -----------------------------
  // Store Type policy (groundwork)
  // -----------------------------
  const STORE_TYPE_POLICY = useMemo(
    () => ({
      pop_up_store: {
        forcedTemplate: { id: "classic-elite", name: "Classic Elite", label: "Classic Elite" },
        allowOrderToProject: (s) => s?.status === "closed", // popup must be closed first
        orderToProjectMode: "popup",
      },
      company_store: {
        forcedTemplate: { id: "custom-elite", name: "Custom Elite", label: "Custom Elite" },
        allowOrderToProject: () => true,
        orderToProjectMode: "company",
      },
      program_store: {
        forcedTemplate: { id: "custom-elite", name: "Custom Elite", label: "Custom Elite" },
        allowOrderToProject: () => false, // program is inventory/replenishment driven later
        orderToProjectMode: "program",
      },
    }),
    []
  );

  const getPolicyForStore = useCallback(
    (s) => {
      const normalizedStoreType = normalizePromoStoreStoreType(s?.StoreType);
      return STORE_TYPE_POLICY[normalizedStoreType] || STORE_TYPE_POLICY[DEFAULT_PROMOSTORE_STORE_TYPE];
    },
    [STORE_TYPE_POLICY]
  );

  const buildForcedTemplateConfig = useCallback(
    (s) => {
      const policy = getPolicyForStore(s);
      const forced = policy.forcedTemplate;

      if (!forced?.id) return s?.TemplateConfig || {};

      return {
        ...(s?.TemplateConfig || {}),
        ...forced,
        forcedByStoreType: true, // important for UI + future backend logic
        forcedAt: new Date().toISOString(),
      };
    },
    [getPolicyForStore]
  );

  // Apply StoreType rules automatically whenever StoreType changes
  useEffect(() => {
    if (!store?.StoreType) return;

    const nextTemplateConfig = buildForcedTemplateConfig(store);

    // Only update if template differs (avoid render loops)
    if ((store?.TemplateConfig?.id || "") !== (nextTemplateConfig?.id || "")) {
      setStore((prev) => ({
        ...prev,
        TemplateConfig: nextTemplateConfig,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store?.StoreType, buildForcedTemplateConfig]);

  // Use this instead of handleStoreChange("StoreType", ...)
  const handleStoreTypeChange = useCallback(
    (nextStoreType) => {
      setStore((prev) => {
        const next = { ...prev, StoreType: normalizePromoStoreStoreType(nextStoreType) };
        return {
          ...next,
          TemplateConfig: buildForcedTemplateConfig(next),
        };
      });
    },
    [buildForcedTemplateConfig]
  );

  const togglePanel = useCallback((panel) => {
    setExpanded((prev) => (prev === panel ? false : panel));
  }, []);

  const onGetWpPages = async () => {
    const url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${store.id}/wp-pages`;
    const r = await axios.get(url, {
      headers: { accessToken: localStorage.getItem("accessToken") },
    });
    return r.data; // { success, pages }
  };
  
  // ONLY when button pressed
  const onRefreshWpPages = async () => {
    const url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${store.id}/wp-pages/refresh`;
    const r = await axios.post(
      url,
      {},
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    return r.data; // { success, pages }
  };  

  // -----------------------------
  // Fetch users (assignments)
  // -----------------------------
  useEffect(() => {
    async function fetchUsers() {
      try {
        const resp = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/auth/byinstanceUniqueId/${instanceUniqueId}`
        );
        setAvailableUsers(resp.data.listOfUsers || []);
      } catch (err) {
        console.error("Error fetching users", err);
      }
    }
    if (instanceUniqueId) fetchUsers();
  }, [instanceUniqueId]);

  // -----------------------------
  // Consolidated store fetch (cancellable)
  // -----------------------------
  const fetchAbortRef = useRef(null);
  const latestStoreRef = useRef(null);

  useEffect(() => {
    latestStoreRef.current = store;
  }, [store]);

  const fetchPromostore = useCallback(async () => {
    if (!id) return;

    // cancel any in-flight request
    if (fetchAbortRef.current) {
      fetchAbortRef.current.abort();
    }
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    try {
      setLoading(true);

      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/get-promostore/${id}`,
        { signal: controller.signal }
      );

      if (response.data?.success) {
        const ps = response.data.promostore;
        const normalizedStore = normalizePromoStoreRecord(ps);

        latestStoreRef.current = normalizedStore;
        setStore(normalizedStore);
        setOrder(normalizedStore || {});
        setPromoStoreCategories(Array.isArray(normalizedStore?.Categories) ? normalizedStore.Categories : []);
        const initialStoreAddress = ps?.TemplateConfig?.storeAddress || ps?.StoreAddress || ps?.BillingAddress || ps?.ShippingAddress || null;
        setStoreAddressId(resolvePromoStoreSelectedAddressId([], initialStoreAddress));

        setAssignedUsers(normalizeAssignedUsersForPromoStore(ps?.AssignedTo));
      } else {
        console.error("Error fetching store details:", response.data?.message);
      }
    } catch (error) {
      if (error?.name !== "CanceledError" && error?.name !== "AbortError") {
        console.error("Error fetching store details:", error);
      }
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchPromostore();
    return () => {
      if (fetchAbortRef.current) fetchAbortRef.current.abort();
    };
  }, [fetchPromostore]);

  // -----------------------------
  // Tabs from route
  // -----------------------------
  useEffect(() => {
    const tabMapping = {
      overview: "Overview",
      files: "Files",
      products: "Products",
      design: "Design",
      orders: "Orders",
      reports: "Reports",
      users: "Users",
    };

    const activeTabFromRoute = tabMapping[tabtype?.toLowerCase()] || "Overview";
    setActiveTab(activeTabFromRoute);
  }, [tabtype]);

  const handleTabChange = useCallback(
    (tab) => {
      const path = `/instance/${instanceUniqueId}/sales/promostores/${id}`;
      switch (tab) {
        case "Products":
          history.push(`${path}/products`);
          break;
        case "Files":
          history.push(`${path}/files`);
          break;
        case "Design":
          history.push(`${path}/design`);
          break;
        case "Orders":
          history.push(`${path}/orders`);
          break;
        case "Reports":
          history.push(`${path}/reports`);
          break;
        case "Users":
          history.push(`${path}/users`);
          break;
        case "Overview":
        default:
          history.push(`${path}/overview`);
          break;
      }
    },
    [history, id, instanceUniqueId]
  );

  // -----------------------------
  // Stable setters
  // -----------------------------
  const handleStoreChange = useCallback((key, value) => {
    setStore((prev) => {
      const base = latestStoreRef.current || prev || {};
      const next = { ...base, [key]: value, isSynced: false };
      latestStoreRef.current = next;
      return next;
    });
  }, []);

const buildNextShippingSettings = useCallback((current, patch) => {
    const merged = { ...current, ...patch };
    const methods = normalizePromoStoreShippingMethods(merged.methods, merged.method);
    const flatRateAmount = merged.flatRateAmount !== undefined && merged.flatRateAmount !== null
      ? String(merged.flatRateAmount)
      : methods.includes("flat_rate")
        ? "0"
        : "";

    return {
      ...current,
      ...patch,
      methods,
      method: methods[0] || DEFAULT_SHIPPING_SETTINGS.method,
      flatRateAmount,
    };
  }, []);

  const handleShippingSettingChange = useCallback((key, value) => {
    setStore((prev) => {
      const current = getPromoStoreShippingSettings(prev);
      return {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, {
          shipping: buildNextShippingSettings(current, { [key]: value }),
        }),
        isSynced: false,
      };
    });
  }, [buildNextShippingSettings]);

  const toggleShippingMethod = useCallback((methodValue) => {
    setStore((prev) => {
      const current = getPromoStoreShippingSettings(prev);
      const nextMethods = current.methods.includes(methodValue)
        ? current.methods.filter((method) => method !== methodValue)
        : [...current.methods, methodValue];

      return {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, {
          shipping: buildNextShippingSettings(current, { methods: nextMethods }),
        }),
        isSynced: false,
      };
    });
  }, [buildNextShippingSettings]);

  const handlePaymentSettingChange = useCallback((key, value) => {
    setStore((prev) => {
      const current = getPromoStorePaymentSettings(prev);
      return {
        ...prev,
        TemplateConfig: normalizePromoStorePaymentGatewayState(
          mergePromoStoreTemplateConfig(prev, {
            payment: { ...current, [key]: !!value },
          }),
        ),
        isSynced: false,
      };
    });
  }, []);

  const handleQuotePurchaseOrderSettingChange = useCallback((section, key, value) => {
    setStore((prev) => {
      const base = latestStoreRef.current || prev || {};
      const current = getPromoStoreQuotePurchaseOrderSettings(base);
      const next = {
        ...base,
        TemplateConfig: mergePromoStoreTemplateConfig(base, {
          [section]: { ...current[section], [key]: value },
        }),
        isSynced: false,
      };
      latestStoreRef.current = next;
      return next;
    });
  }, []);

  const handleEmailNotificationChange = useCallback((notificationKey, field, value) => {
    setStore((prev) => {
      const current = getPromoStoreEmailNotifications(prev);
      return {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, {
          emailNotifications: {
            ...current,
            [notificationKey]: {
              ...current[notificationKey],
              [field]: value,
            },
          },
        }),
        isSynced: false,
      };
    });
  }, []);

  // Keep the async customer selector onChange stable.
  const attachedCustomerId = store?.attachedCustomers?.[0] ?? "";

  const syncCheckoutAddressSettingsForCustomer = useCallback((contacts, addresses) => {
    setStore((prev) => {
      const current = getPromoStoreCheckoutAddressSettings(prev);
      const nextSettings = reconcilePromoStoreCheckoutAddressSettings(current, contacts, addresses);
      if (arePromoStoreCheckoutAddressSettingsEqual(current, nextSettings)) {
        return prev;
      }

      const next = {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, { checkoutAddresses: nextSettings }),
        isSynced: false,
      };
      latestStoreRef.current = next;
      return next;
    });
  }, []);

  const handleCustomerChange = useCallback((customerId) => {
    setStore((prev) => {
      const next = {
        ...prev,
        attachedCustomers: customerId ? [customerId] : [],
        isSynced: false,
      };
      latestStoreRef.current = next;
      return next;
    });

    setClientAddresses([]);
    setClientContacts([]);
    setStoreAddressId(null);
  }, []);

  // -----------------------------
  // Customer details (cache + cancellable)
  // -----------------------------
  const customerAbortRef = useRef(null);

  useEffect(() => {
    const cid = attachedCustomerId;
    if (!cid) {
      setClientAddresses([]);
      setClientContacts([]);
      setStoreAddressId(null);
      syncCheckoutAddressSettingsForCustomer([], []);
      return;
    }

    if (customerAbortRef.current) {
      customerAbortRef.current.abort();
    }
    const controller = new AbortController();
    customerAbortRef.current = controller;

    async function run() {
      try {
        if (customerDetailsCache.has(cid)) {
          const cached = customerDetailsCache.get(cid);
          setClientAddresses(cached.formatted);
          setClientContacts(cached.contacts || []);
          syncCheckoutAddressSettingsForCustomer(cached.contacts || [], cached.formatted);

          const existingStoreAddress = store?.TemplateConfig?.storeAddress || store?.StoreAddress || store?.BillingAddress || store?.ShippingAddress || null;
          if (existingStoreAddress) setStoreAddressId(resolvePromoStoreSelectedAddressId(cached.formatted, existingStoreAddress));
          return;
        }

        const res = await getCustomerDetails(cid, { signal: controller.signal });

        const rawAddresses = res?.customer?.address || [];
        const formatted = rawAddresses.map((addr) => {
          const stableId = getPromoStoreCustomerAddressId(addr);
          const legacyId = convertAddressToString(addr);
          return {
            id: stableId || legacyId,
            legacyId,
            approvedId: stableId || legacyId,
            name: addr.addressName || addr.name || "",
            label: formatCustomerAddressLabel(addr),
            summary: formatPromoStoreAddressLine(addr),
            address: addr,
          };
        });

        const formattedContacts = (res?.customer?.clientContacts || []).map((contact) => ({
          id: getPromoStoreContactId(contact),
          label: formatPromoStoreContactLabel(contact),
          contact,
        })).filter((contact) => contact.id);

        customerDetailsCache.set(cid, { formatted, contacts: formattedContacts });
        setClientAddresses(formatted);
        setClientContacts(formattedContacts);
        syncCheckoutAddressSettingsForCustomer(formattedContacts, formatted);

        const existingStoreAddress = store?.TemplateConfig?.storeAddress || store?.StoreAddress || store?.BillingAddress || store?.ShippingAddress || null;
        if (existingStoreAddress) setStoreAddressId(resolvePromoStoreSelectedAddressId(formatted, existingStoreAddress));
      } catch (err) {
        if (err?.name !== "AbortError" && err?.name !== "CanceledError") {
          console.error(err);
        }
      }
    }

    run();
    return () => controller.abort();
    // only re-run when actual selected customer changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [attachedCustomerId]);


  const onFetchWpPages = async () => {
    const url = `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${instanceUniqueId}/${store.id}/wp-pages/refresh`;
    const r = await axios.post(
      url,
      {},
      { headers: { accessToken: localStorage.getItem("accessToken") } }
    );
    return r.data; // { success, pages }
  };


  const archiveConfirmTarget = store?.ShopName || store?.path || "DELETE";
  const canConfirmArchive = archiveConfirmText.trim() === "DELETE" || archiveConfirmText.trim() === archiveConfirmTarget;

  const handleArchivePromoStore = async () => {
    if (!isAdministrator) {
      setArchiveMessage({ severity: "info", text: "Please contact an administrator to delete/archive this PromoStore." });
      return;
    }
    if (!canConfirmArchive) return;
    setArchiveWorking(true);
    setArchiveMessage(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/archive`,
        {},
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setArchiveMessage({ severity: "success", text: response.data?.message || "PromoStore archived." });
      history.push(`/instance/${instanceUniqueId}/sales/promostores`);
    } catch (error) {
      setArchiveMessage({
        severity: "error",
        text: error?.response?.data?.message || "Failed to archive PromoStore.",
      });
    } finally {
      setArchiveWorking(false);
    }
  };

  const handleRestorePromoStore = async () => {
    if (!isAdministrator) {
      setArchiveMessage({ severity: "info", text: "Please contact an administrator to restore this PromoStore." });
      return;
    }
    setArchiveWorking(true);
    setArchiveMessage(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/restore`,
        {},
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setArchiveMessage({ severity: response.data?.wordpress?.succeeded === false ? "warning" : "success", text: response.data?.message || "PromoStore restored." });
      await fetchPromostore();
    } catch (error) {
      setArchiveMessage({
        severity: "error",
        text: error?.response?.data?.message || "Failed to restore PromoStore.",
      });
    } finally {
      setArchiveWorking(false);
    }
  };

  // -----------------------------
  // Actions / Products
  // -----------------------------
  const handleActionsClick = useCallback(() => {
    setShowActions((prev) => !prev);
  }, []);

  const handleShowProductsPopup = useCallback(() => {
    setShowProductManagerPopup((prev) => !prev);
  }, []);

  const closeProductListPopup = useCallback(() => {
    setShowProductManagerPopup(false);
  }, []);

  const handleSaveProduct = useCallback(async (updatedProductData) => {
    setOrder((prevOrder) => ({
      ...prevOrder,
      Products: (prevOrder?.Products || []).map((prod) =>
        (prod.ProductId || prod.Id) === (updatedProductData.ProductId || updatedProductData.Id)
          ? { ...prod, ...updatedProductData }
          : prod
      ),
    }));
    alert("Save changes :)");
  }, []);

  const handleAssignedUsersChange = useCallback((newArr) => {
    setAssignedUsers(newArr);
  
    const minimized = toMinAssignedUsers(newArr);
  
    setStore((prev) => {
      if (JSON.stringify(prev.AssignedTo || []) === JSON.stringify(minimized)) return prev;
      return { ...prev, AssignedTo: minimized, isSynced: false };
    });
  }, []);
  

  const updateItemsToOrder = useCallback(
    async (products) => {
      try {
        const payload = {
          customer_name: order.CustomerName,
          email: order.Email,
          phone: order.Phone,
          total: order.Total,
          items: products || [],
          siteURL: order.SiteURL,
          order_status: order.OrderStatus,
          assigned_to: toMinAssignedUsers(assignedUsers),
          AssignedTo: toMinAssignedUsers(assignedUsers),
          AttachedCustomerId:
            order?.attachedCustomers?.length > 0 ? order?.attachedCustomers[0] : "",
        };

        await axios.put(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/edit-promostore/${id}`,
          payload
        );

        await fetchPromostore();
      } catch (error) {
        console.log(error);
      }
    },
    [assignedUsers, fetchPromostore, id, order]
  );

  const syncPromoStoreCategoriesState = useCallback((nextCategories) => {
    const normalizedCategories = Array.isArray(nextCategories) ? nextCategories : [];
    const categoriesById = new Map(
      normalizedCategories.map((category) => [String(category.id), category])
    );
    const syncProducts = (products = []) => (
      (Array.isArray(products) ? products : []).map((product) => {
        const sourceCategoryIds = Array.isArray(product?.categoryIds)
          ? product.categoryIds
          : Array.isArray(product?.CategoryIds)
            ? product.CategoryIds
            : Array.isArray(product?.categories)
              ? product.categories.map((category) => category?.id)
              : Array.isArray(product?.Categories)
                ? product.Categories.map((category) => category?.id)
                : (
                    product?.categoryId ??
                    product?.CategoryId ??
                    product?.category?.id ??
                    product?.Category?.id
                  )
                  ? [
                      product?.categoryId ??
                      product?.CategoryId ??
                      product?.category?.id ??
                      product?.Category?.id,
                    ]
                  : [];
        const matchedCategories = sourceCategoryIds
          .map((categoryId) => categoriesById.get(String(categoryId)) || null)
          .filter(Boolean);
        return {
          ...product,
          categoryIds: matchedCategories.map((category) => category.id),
          CategoryIds: matchedCategories.map((category) => category.id),
          categories: matchedCategories,
          Categories: matchedCategories,
        };
      })
    );

    setPromoStoreCategories(normalizedCategories);
    setStore((prev) => ({
      ...prev,
      Categories: normalizedCategories,
      Products: syncProducts(prev?.Products),
      Items: syncProducts(prev?.Items),
    }));
    setOrder((prev) => ({
      ...prev,
      Categories: normalizedCategories,
      Products: syncProducts(prev?.Products),
      Items: syncProducts(prev?.Items),
    }));
  }, []);

  const handleCreatePromoStoreCategory = useCallback(async (name) => {
    try {
      setCategorySaving(true);
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/categories`,
        { name }
      );
      syncPromoStoreCategoriesState(response.data?.categories || []);
      return true;
    } catch (error) {
      alert(error?.response?.data?.message || error?.message || "Failed to create category.");
      return false;
    } finally {
      setCategorySaving(false);
    }
  }, [id, syncPromoStoreCategoriesState]);

  const handleRenamePromoStoreCategory = useCallback(async (categoryId, name) => {
    try {
      setCategorySaving(true);
      const response = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/categories/${categoryId}`,
        { name }
      );
      syncPromoStoreCategoriesState(response.data?.categories || []);
      return true;
    } catch (error) {
      alert(error?.response?.data?.message || error?.message || "Failed to rename category.");
      return false;
    } finally {
      setCategorySaving(false);
    }
  }, [id, syncPromoStoreCategoriesState]);

  const handleMovePromoStoreCategory = useCallback(async (categoryId, direction) => {
    const currentCategories = Array.isArray(promoStoreCategories) ? promoStoreCategories : [];
    const currentIndex = currentCategories.findIndex((category) => String(category.id) === String(categoryId));
    const targetIndex = currentIndex + direction;
    if (currentIndex < 0 || targetIndex < 0 || targetIndex >= currentCategories.length) return false;

    const nextCategories = [...currentCategories];
    [nextCategories[currentIndex], nextCategories[targetIndex]] = [nextCategories[targetIndex], nextCategories[currentIndex]];

    try {
      setCategorySaving(true);
      const response = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/categories/reorder`,
        { categoryIds: nextCategories.map((category) => category.id) }
      );
      syncPromoStoreCategoriesState(response.data?.categories || []);
      return true;
    } catch (error) {
      alert(error?.response?.data?.message || error?.message || "Failed to reorder categories.");
      return false;
    } finally {
      setCategorySaving(false);
    }
  }, [id, promoStoreCategories, syncPromoStoreCategoriesState]);

  const handleDeletePromoStoreCategory = useCallback(async (category) => {
    const assignedCount = (Array.isArray(order?.Products) ? order.Products : []).filter((product) => {
      const categoryIds = Array.isArray(product?.categoryIds)
        ? product.categoryIds
        : Array.isArray(product?.CategoryIds)
          ? product.CategoryIds
          : Array.isArray(product?.categories)
            ? product.categories.map((entry) => entry?.id)
            : Array.isArray(product?.Categories)
              ? product.Categories.map((entry) => entry?.id)
              : [];
      return categoryIds.some((categoryId) => String(categoryId || "") === String(category?.id || ""));
    }).length;

    const confirmed = window.confirm(
      `Delete "${category?.name || "this category"}"?\n\n${assignedCount} products currently use this category.\n\nThe products will remain in the PromoStore and will be changed to No Category.`
    );
    if (!confirmed) return false;

    try {
      setCategorySaving(true);
      const response = await axios.delete(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/categories/${category.id}`
      );
      syncPromoStoreCategoriesState(response.data?.categories || []);
      return true;
    } catch (error) {
      alert(error?.response?.data?.message || error?.message || "Failed to delete category.");
      return false;
    } finally {
      setCategorySaving(false);
    }
  }, [id, order?.Products, syncPromoStoreCategoriesState]);
  const checkoutAddressSettings = getPromoStoreCheckoutAddressSettings(store);
  const checkoutAddressMode = checkoutAddressSettings.checkoutAddressMode;
  const approvedShippingAddressIds = checkoutAddressSettings.approvedShippingAddressIds;
  const saveNewCustomerAddresses = checkoutAddressSettings.saveNewCustomerAddresses;
  const billingAddressMode = checkoutAddressSettings.billingAddressMode;
  const approvedBillingAddressIds = checkoutAddressSettings.approvedBillingAddressIds;
  const saveNewBillingAddresses = checkoutAddressSettings.saveNewBillingAddresses;
  const billingContactMode = checkoutAddressSettings.billingContactMode;
  const approvedBillingContactIds = checkoutAddressSettings.approvedBillingContactIds;
  const saveNewBillingContacts = checkoutAddressSettings.saveNewBillingContacts;
  const shippingContactMode = checkoutAddressSettings.shippingContactMode;
  const approvedShippingContactIds = checkoutAddressSettings.approvedShippingContactIds;
  const saveNewShippingContacts = checkoutAddressSettings.saveNewShippingContacts;
  const checkoutQuestions = getPromoStoreCheckoutQuestions(store);

  const updateCheckoutAddressSettings = useCallback((patch) => {
    setStore((prev) => {
      const current = getPromoStoreCheckoutAddressSettings(prev);
      const nextSettings = {
        ...current,
        ...patch,
      };

      PROMOSTORE_CHECKOUT_MODE_SAVE_KEY_PAIRS.forEach(([modeKey, saveKey]) => {
        nextSettings[modeKey] = normalizePromoStoreCheckoutAddressMode(nextSettings[modeKey]);
        nextSettings[saveKey] = nextSettings[saveKey] === true;
      });

      nextSettings.approvedShippingAddressIds = normalizePromoStoreSelectionIds(nextSettings.approvedShippingAddressIds);
      nextSettings.approvedBillingAddressIds = normalizePromoStoreSelectionIds(nextSettings.approvedBillingAddressIds);
      nextSettings.approvedBillingContactIds = normalizePromoStoreSelectionIds(nextSettings.approvedBillingContactIds);
      nextSettings.approvedShippingContactIds = normalizePromoStoreSelectionIds(nextSettings.approvedShippingContactIds);
      nextSettings.approvedShippingAddressSnapshots = normalizePromoStoreApprovedSnapshots(nextSettings.approvedShippingAddressSnapshots);
      nextSettings.approvedBillingAddressSnapshots = normalizePromoStoreApprovedSnapshots(nextSettings.approvedBillingAddressSnapshots);
      nextSettings.approvedBillingContactSnapshots = normalizePromoStoreApprovedSnapshots(nextSettings.approvedBillingContactSnapshots);
      nextSettings.approvedShippingContactSnapshots = normalizePromoStoreApprovedSnapshots(nextSettings.approvedShippingContactSnapshots);

      const next = {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, { checkoutAddresses: nextSettings }),
        isSynced: false,
      };
      latestStoreRef.current = next;
      return next;
    });
  }, []);

  const handleCheckoutSectionModeChange = useCallback((modeKey, value) => {
    updateCheckoutAddressSettings({
      [modeKey]: normalizePromoStoreCheckoutAddressMode(value),
    });
  }, [updateCheckoutAddressSettings]);

  const updateCheckoutQuestions = useCallback((nextQuestions) => {
    const normalizedQuestions = normalizePromoStoreCheckoutQuestions(nextQuestions);
    setStore((prev) => {
      const next = {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, { checkoutQuestions: normalizedQuestions }),
        isSynced: false,
      };
      latestStoreRef.current = next;
      return next;
    });
  }, []);

  const resetCheckoutQuestionEditor = useCallback(() => {
    setCheckoutQuestionDraft(EMPTY_CHECKOUT_QUESTION_DRAFT);
    setEditingCheckoutQuestionId("");
    setCheckoutQuestionError("");
  }, []);

  const getCheckoutQuestionDraftValidation = useCallback(() => {
    const question = checkoutQuestionDraft.question.trim();
    if (!question) return "Checkout question text is required.";
    if (checkoutQuestionDraft.type === "dropdown") {
      const rawOptions = checkoutQuestionDraft.optionsText.split("\n").map((option) => option.trim()).filter(Boolean);
      if (rawOptions.length > 100) return "A maximum of 100 dropdown options is allowed.";
      if (rawOptions.some((option) => option.length > 150)) return "Dropdown options must be 150 characters or fewer.";
      const options = normalizePromoStoreCheckoutQuestionOptions(rawOptions);
      if (!options.length) return "Dropdown checkout questions require at least one option.";
    }
    return "";
  }, [checkoutQuestionDraft]);

  const handleCheckoutQuestionSubmit = useCallback(() => {
    const validationMessage = getCheckoutQuestionDraftValidation();
    if (validationMessage) {
      setCheckoutQuestionError(validationMessage);
      return;
    }

    const record = {
      id: editingCheckoutQuestionId || createPromoStoreCheckoutQuestionId(),
      question: checkoutQuestionDraft.question,
      type: checkoutQuestionDraft.type,
      mandatory: checkoutQuestionDraft.mandatory,
      options: checkoutQuestionDraft.type === "dropdown"
        ? checkoutQuestionDraft.optionsText.split("\n")
        : [],
    };
    const nextQuestions = editingCheckoutQuestionId
      ? checkoutQuestions.map((question) => question.id === editingCheckoutQuestionId ? record : question)
      : [...checkoutQuestions, record];
    updateCheckoutQuestions(nextQuestions);
    resetCheckoutQuestionEditor();
  }, [checkoutQuestionDraft, checkoutQuestions, editingCheckoutQuestionId, getCheckoutQuestionDraftValidation, resetCheckoutQuestionEditor, updateCheckoutQuestions]);

  const handleCheckoutQuestionEdit = useCallback((question) => {
    setEditingCheckoutQuestionId(question.id);
    setCheckoutQuestionDraft({
      question: question.question,
      type: question.type,
      mandatory: question.mandatory,
      optionsText: question.options.join("\n"),
    });
    setCheckoutQuestionError("");
  }, []);

  const handleCheckoutQuestionDelete = useCallback((questionId) => {
    if (!window.confirm("Remove this checkout question?")) return;
    updateCheckoutQuestions(checkoutQuestions.filter((question) => question.id !== questionId));
    if (editingCheckoutQuestionId === questionId) resetCheckoutQuestionEditor();
  }, [checkoutQuestions, editingCheckoutQuestionId, resetCheckoutQuestionEditor, updateCheckoutQuestions]);

  const handleCheckoutQuestionMove = useCallback((index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= checkoutQuestions.length) return;
    const reordered = [...checkoutQuestions];
    [reordered[index], reordered[targetIndex]] = [reordered[targetIndex], reordered[index]];
    updateCheckoutQuestions(reordered);
  }, [checkoutQuestions, updateCheckoutQuestions]);
  const getApprovedContactOptions = useCallback((ids = []) => (
    normalizePromoStoreSelectionIds(ids)
      .map((contactId) => findPromoStoreClientContact(clientContacts, contactId))
      .filter(Boolean)
  ), [clientContacts]);

  const refreshAttachedCustomerContacts = useCallback(async () => {
    if (!attachedCustomerId) {
      setClientContacts([]);
      syncCheckoutAddressSettingsForCustomer([], clientAddresses);
      return [];
    }

    const response = await getClientContacts(attachedCustomerId);
    const refreshedContacts = formatPromoStoreClientContactOptions(response?.data?.clientContacts || []);
    // The lightweight contact endpoint intentionally returns only active
    // contacts, but existing PromoStore checkout settings can reference a
    // historical alias. Retain cached aliases when refreshing the picker so
    // an unrelated store edit never strips approved billing/shipping IDs.
    const existingAliases = clientContacts.filter((entry) =>
      entry?.contact?.mergedIntoContactId &&
      !refreshedContacts.some((fresh) => fresh.id === entry.id));
    const withHistoricalAliases = [...refreshedContacts, ...existingAliases];
    setClientContacts(withHistoricalAliases);
    syncCheckoutAddressSettingsForCustomer(withHistoricalAliases, clientAddresses);
    return withHistoricalAliases;
  }, [attachedCustomerId, clientAddresses, clientContacts, syncCheckoutAddressSettingsForCustomer]);

  const saveApprovedContactSelection = useCallback((idsKey, snapshotsKey, nextIds) => {
    updateCheckoutAddressSettings({
      [idsKey]: nextIds,
      [snapshotsKey]: buildPromoStoreApprovedContactSnapshots(clientContacts, nextIds),
    });
  }, [clientContacts, updateCheckoutAddressSettings]);

  const saveApprovedAddressSelection = useCallback((idsKey, snapshotsKey, nextIds) => {
    updateCheckoutAddressSettings({
      [idsKey]: nextIds,
      [snapshotsKey]: buildPromoStoreApprovedAddressSnapshots(clientAddresses, nextIds),
    });
  }, [clientAddresses, updateCheckoutAddressSettings]);

  const openApprovedListDialog = useCallback((config) => {
    setApprovedListDialogState(config);
    setApprovedListDraftIds(Array.isArray(config?.selectedIds) ? [...config.selectedIds] : []);
    setApprovedListCandidateId('');
  }, []);

  const closeApprovedListDialog = useCallback(() => {
    setApprovedListDialogState(null);
    setApprovedListDraftIds([]);
    setApprovedListCandidateId('');
  }, []);

  const addApprovedListDraftId = useCallback(() => {
    if (!approvedListCandidateId) return;
    setApprovedListDraftIds((prev) => (prev.includes(approvedListCandidateId) ? prev : [...prev, approvedListCandidateId]));
    setApprovedListCandidateId('');
  }, [approvedListCandidateId]);

  const removeApprovedListDraftId = useCallback((itemId) => {
    setApprovedListDraftIds((prev) => prev.filter((id) => id !== itemId));
  }, []);

  const handleApprovedListDialogSave = useCallback(() => {
    if (approvedListDialogState?.onSave) {
      approvedListDialogState.onSave(approvedListDraftIds);
    }
    closeApprovedListDialog();
  }, [approvedListDialogState, approvedListDraftIds, closeApprovedListDialog]);

  const openContactManagementDialog = useCallback((config) => {
    setContactManagementDialogState(config);
    setContactManagementDraftIds(Array.isArray(config?.selectedIds) ? [...config.selectedIds] : []);
    setContactManagementCandidateId('');
  }, []);

  const closeContactManagementDialog = useCallback(() => {
    setContactManagementDialogState(null);
    setContactManagementDraftIds([]);
    setContactManagementCandidateId('');
  }, []);

  const addContactManagementDraftId = useCallback(() => {
    if (!contactManagementCandidateId) return;
    setContactManagementDraftIds((prev) => (prev.includes(contactManagementCandidateId) ? prev : [...prev, contactManagementCandidateId]));
    setContactManagementCandidateId('');
  }, [contactManagementCandidateId]);

  const removeContactManagementDraftId = useCallback((itemId) => {
    setContactManagementDraftIds((prev) => prev.filter((id) => id !== itemId));
  }, []);

  const handleContactManagementDialogSave = useCallback(() => {
    if (contactManagementDialogState?.onSave) {
      contactManagementDialogState.onSave(contactManagementDraftIds);
    }
    closeContactManagementDialog();
  }, [closeContactManagementDialog, contactManagementDialogState, contactManagementDraftIds]);

  const handleManagedContactRemove = useCallback((contactOption) => {
    const optionId = normalizePromoStoreId(contactOption?.id);
    if (!optionId) return;

    removeContactManagementDraftId(optionId);
  }, [removeContactManagementDraftId]);
  const checkoutApprovalSections = [
    {
      key: 'billing-contacts',
      group: 'contacts',
      title: 'Billing Name',
      selectedIds: approvedBillingContactIds,
      mode: billingContactMode,
      editListLabel: 'Edit Billing Contact List',
      emptyMessage: 'No contacts are available for this customer.',
      saveNewValue: saveNewBillingContacts,
      saveNewLabel: "Add these new contacts to the customer's contact list upon checkout",
      openLabel: 'Allow any customer contact',
      approvedOnlyLabel: 'Selecting from list',
      approvedOrNewLabel: 'Select or enter a new contact',
      onModeChange: (value) => handleCheckoutSectionModeChange('billingContactMode', value),
      onSelectionSave: (nextIds) => saveApprovedContactSelection(
        'approvedBillingContactIds',
        'approvedBillingContactSnapshots',
        nextIds
      ),
      onSaveNewChange: (checked) => updateCheckoutAddressSettings({ saveNewBillingContacts: checked }),
      managementTitle: 'Manage Billing Contacts',
      approvedIdsKey: 'approvedBillingContactIds',
    },
    {
      key: 'shipping-contacts',
      group: 'contacts',
      title: 'Shipping Name',
      selectedIds: approvedShippingContactIds,
      mode: shippingContactMode,
      editListLabel: 'Edit Shipping Contact List',
      emptyMessage: 'No contacts are available for this customer.',
      saveNewValue: saveNewShippingContacts,
      saveNewLabel: "Add these new contacts to the customer's contact list upon checkout",
      openLabel: 'Allow any customer contact',
      approvedOnlyLabel: 'Selecting from list',
      approvedOrNewLabel: 'Select or enter a new contact',
      onModeChange: (value) => handleCheckoutSectionModeChange('shippingContactMode', value),
      onSelectionSave: (nextIds) => saveApprovedContactSelection(
        'approvedShippingContactIds',
        'approvedShippingContactSnapshots',
        nextIds
      ),
      onSaveNewChange: (checked) => updateCheckoutAddressSettings({ saveNewShippingContacts: checked }),
      managementTitle: 'Manage Shipping Contacts',
      approvedIdsKey: 'approvedShippingContactIds',
    },
    {
      key: 'billing-addresses',
      group: 'addresses',
      title: 'Billing Address',
      selectedIds: approvedBillingAddressIds,
      mode: billingAddressMode,
      availableItems: clientAddresses,
      editListLabel: 'Edit Billing Address List',
      emptyMessage: 'No customer addresses are available.',
      saveNewValue: saveNewBillingAddresses,
      saveNewLabel: "Add these new addresses to the customer's address list upon checkout",
      openLabel: 'Allow any customer address',
      approvedOnlyLabel: 'Selecting from list',
      approvedOrNewLabel: 'Select or enter a new address',
      onModeChange: (value) => handleCheckoutSectionModeChange('billingAddressMode', value),
      onSelectionSave: (nextIds) => saveApprovedAddressSelection(
        'approvedBillingAddressIds',
        'approvedBillingAddressSnapshots',
        nextIds
      ),
      onSaveNewChange: (checked) => updateCheckoutAddressSettings({ saveNewBillingAddresses: checked }),
    },
    {
      key: 'shipping-addresses',
      group: 'addresses',
      title: 'Shipping Address',
      selectedIds: approvedShippingAddressIds,
      mode: checkoutAddressMode,
      availableItems: clientAddresses,
      editListLabel: 'Edit Shipping Address List',
      emptyMessage: 'No customer addresses are available.',
      saveNewValue: saveNewCustomerAddresses,
      saveNewLabel: "Add these new addresses to the customer's address list upon checkout",
      openLabel: 'Allow any customer address',
      approvedOnlyLabel: 'Selecting from list',
      approvedOrNewLabel: 'Select or enter a new address',
      onModeChange: (value) => handleCheckoutSectionModeChange('checkoutAddressMode', value),
      onSelectionSave: (nextIds) => saveApprovedAddressSelection(
        'approvedShippingAddressIds',
        'approvedShippingAddressSnapshots',
        nextIds
      ),
      onSaveNewChange: (checked) => updateCheckoutAddressSettings({ saveNewCustomerAddresses: checked }),
    },
  ];

  const renderCheckoutApprovalSection = ({
    key,
    group,
    title,
    selectedIds,
    mode,
    availableItems = [],
    editListLabel,
    emptyMessage,
    saveNewValue,
    saveNewLabel,
    openLabel,
    approvedOnlyLabel,
    approvedOrNewLabel,
    onModeChange,
    onSelectionSave,
    onSaveNewChange,
    managementTitle = '',
    approvedIdsKey = '',
  }) => {
    const showSaveNewToggle = mode === 'approved_or_new';
    const isOpenMode = mode === 'open';
    const isContactSection = group === 'contacts';
    const selectedCount = normalizePromoStoreSelectionIds(selectedIds).length;

    const handleEditList = () => {
      if (isContactSection) {
        openContactManagementDialog({
          title: managementTitle,
          approvedIdsKey,
          emptyMessage,
          selectedIds,
          onSave: onSelectionSave,
        });
        return;
      }

      openApprovedListDialog({
        key,
        title: editListLabel,
        availableItems,
        selectedIds,
        emptyMessage,
        onSave: onSelectionSave,
      });
    };

    return (
      <div key={key} className="field promostore-approval-section">
        <div className="promostore-approval-section-header">
          <span className="promostore-approval-section-title">{title}</span>
        </div>

        <div className="promostore-approval-mode-group" role="radiogroup" aria-label={`${title} mode`}>
          <label className="promostore-compact-choice">
            <input
              type="radio"
              name={`${key}-mode`}
              value="approved_only"
              checked={mode === 'approved_only'}
              onChange={(e) => onModeChange(e.target.value)}
            />
            {approvedOnlyLabel}
          </label>

          <label className="promostore-compact-choice">
            <input
              type="radio"
              name={`${key}-mode`}
              value="approved_or_new"
              checked={mode === 'approved_or_new'}
              onChange={(e) => onModeChange(e.target.value)}
            />
            {approvedOrNewLabel}
          </label>
        </div>

        <details className="promostore-approval-advanced" open={isOpenMode}>
          <summary>Advanced options</summary>
          <label className="promostore-compact-choice promostore-approval-open-choice">
            <input
              type="radio"
              name={`${key}-mode`}
              value="open"
              checked={isOpenMode}
              onChange={(e) => onModeChange(e.target.value)}
            />
            {openLabel}
          </label>
          {isOpenMode && (
            <small className="field-helper-text">
              The approved list and saved compatibility values remain preserved while this mode is active.
            </small>
          )}
        </details>

        <div className="promostore-approval-actions">
          <Button variant="contained" size="small" onClick={handleEditList}>
            {editListLabel} ({selectedCount})
          </Button>
          {showSaveNewToggle && (
            <label className="promostore-approval-save-new promostore-compact-choice">
              <input
                type="checkbox"
                checked={saveNewValue}
                onChange={(e) => onSaveNewChange(e.target.checked)}
              />
              {saveNewLabel}
            </label>
          )}
          {!selectedCount && (
            <small className="field-helper-text">{emptyMessage}</small>
          )}
        </div>
      </div>
    );
  };

  const currentApprovedListOptions = approvedListDialogState
    ? normalizePromoStoreSelectionIds(approvedListDraftIds)
      .map((itemId) => approvedListDialogState.availableItems?.find((item) => item.id === itemId))
      .filter(Boolean)
    : [];
  const availableApprovedListCandidates = approvedListDialogState
    ? (approvedListDialogState.availableItems || []).filter((item) => !approvedListDraftIds.includes(item.id))
    : [];
  const currentManagedContactOptions = contactManagementDialogState
    ? getApprovedContactOptions(contactManagementDraftIds)
    : [];
  const availableManagedContactCandidates = contactManagementDialogState
    ? clientContacts.filter((contact) => !contact?.contact?.mergedIntoContactId && !contactManagementDraftIds.includes(contact.id))
    : [];

  // -----------------------------
  // Sync

  // -----------------------------
  const runSync = useCallback(async () => {
    setSyncing(true);
    try {
      const syncStore = latestStoreRef.current || store;
      const syncCheckoutAddressSettings = getPromoStoreCheckoutAddressSettings(syncStore);
      const syncCheckoutQuestions = getPromoStoreCheckoutQuestions(syncStore);
      const syncQuotePurchaseOrderSettings = getPromoStoreQuotePurchaseOrderSettings(syncStore);
      const templateConfig = syncStore.TemplateConfig || {};
      const shipping = getPromoStoreShippingSettings(syncStore);
      const payment = getPromoStorePaymentSettings(syncStore);
      const emailNotifications = getPromoStoreEmailNotifications(syncStore);
      const selectedStoreAddress = findPromoStoreClientAddress(clientAddresses, storeAddressId)?.address || null;
      const banners = {
        ...(templateConfig.banners || {}),
        mainBannerUrl: syncStore.HomepageBannerUrl || templateConfig.banners?.mainBannerUrl || "",
        cartBannerUrl: templateConfig.banners?.cartBannerUrl || "",
        myAccountBannerUrl: templateConfig.banners?.myAccountBannerUrl || "",
        productPageBannerUrl: templateConfig.banners?.productPageBannerUrl || "",
      };
      const syncTemplateConfig = normalizePromoStorePaymentGatewayState({
        ...templateConfig,
        banners,
        shipping,
        payment,
        emailNotifications,
        storeAddress: selectedStoreAddress,
        checkoutAddresses: syncCheckoutAddressSettings,
        checkoutQuestions: syncCheckoutQuestions,
        ...syncQuotePurchaseOrderSettings,
      });

      let response;
      if (syncDirection === "wordpress_to_core") {
        response = await axios.post(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/sync-settings-from-wordpress`,
          {},
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );
      } else {
        response = await axios.post(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/sync-products`,
          {
            shopName: syncStore.ShopName,
            homepageBannerUrl: buildPromoStoreBrandingSyncPayload(syncStore).homepageBannerUrl,
            banners,
            shipping,
            payment,
            emailNotifications,
            storeAddress: selectedStoreAddress,
            billingAddress: selectedStoreAddress,
            shippingAddress: selectedStoreAddress,
            calculateTaxes: syncStore.CalculateTaxes || false,
            expiryDate: syncStore.ExpiryDate || null,
            expiryTime: syncStore.ExpiryTime || "23:59",
            storeType: syncStore.StoreType || null,
            currency: syncStore.Currency || null,
            primaryColor: syncStore.PrimaryColor || null,
            secondaryColor: syncStore.SecondaryColor || null,
            templateConfig: syncTemplateConfig,
            shopLogoUrl: buildPromoStoreBrandingSyncPayload(syncStore).shopLogoUrl,
            products: order.Products || [],
          }
        );
      }

      if (response.data?.promostore) {
        const normalizedStore = normalizePromoStoreRecord(response.data.promostore);
        latestStoreRef.current = normalizedStore;
        setStore(normalizedStore);
      } else {
        fetchPromostore();
      }
      const orderWarning = response.data?.orderSyncWarning;
      alert(
        syncDirection === "wordpress_to_core"
          ? "WordPress settings were synced into Elite Core."
          : orderWarning
            ? `Store settings & products synced. ${orderWarning}`
            : "All store settings & products have been queued for sync!"
      );
      setSyncDialogOpen(false);
    } catch (err) {
      console.error(err);
      alert(
        err?.response?.data?.message ||
          "Sync failed. Check console for details."
      );
    } finally {
      setSyncing(false);
    }
  }, [clientAddresses, fetchPromostore, id, order.Products, store, storeAddressId, syncDirection]);

  const handleInitiateSync = useCallback(() => {
    setSyncDirection("core_to_wordpress");
    setSyncDialogOpen(true);
  }, []);

const toMinAssignedUsers = (arr) => {
  if (!Array.isArray(arr)) return [];

  return arr
    .filter(Boolean)
    .map((u) => {
      const source = u && typeof u === "object" ? u : { id: u };
      const idNum = Number(source.id);

      // Only include the fields you want persisted
      const out = {
        id: Number.isFinite(idNum) ? idNum : source.id, // fallback if weird
        role: source.role || "customer",
      };

      // Only include first_name if it exists
      if (source.first_name) out.first_name = source.first_name;

      return out;
    });
};


  // -----------------------------
  // Overview save
  // -----------------------------
  const handleSaveOverview = useCallback(async () => {
    try {
      const draftStore = latestStoreRef.current || store;
      const templateConfig = draftStore.TemplateConfig || {};
      const shipping = getPromoStoreShippingSettings(draftStore);
      const payment = getPromoStorePaymentSettings(draftStore);
      const emailNotifications = getPromoStoreEmailNotifications(draftStore);
      const overviewCheckoutQuestions = getPromoStoreCheckoutQuestions(draftStore);
      const overviewQuotePurchaseOrderSettings = getPromoStoreQuotePurchaseOrderSettings(draftStore);
      const selectedStoreAddress = findPromoStoreClientAddress(clientAddresses, storeAddressId)?.address || null;
      const banners = {
        ...(templateConfig.banners || {}),
        mainBannerUrl: draftStore.HomepageBannerUrl || templateConfig.banners?.mainBannerUrl || "",
        cartBannerUrl: templateConfig.banners?.cartBannerUrl || "",
        myAccountBannerUrl: templateConfig.banners?.myAccountBannerUrl || "",
        productPageBannerUrl: templateConfig.banners?.productPageBannerUrl || "",
      };
      const overviewTemplateConfig = normalizePromoStorePaymentGatewayState({
        ...templateConfig,
        banners,
        shipping,
        payment,
        emailNotifications,
        storeAddress: selectedStoreAddress,
        checkoutAddresses: checkoutAddressSettings,
        checkoutQuestions: overviewCheckoutQuestions,
        ...overviewQuotePurchaseOrderSettings,
      });

      const payload = {
        updateScope: "overview",
        shopName: draftStore.ShopName,
        shopContactPhone: draftStore.Phone,
        status: draftStore.status,
        storeType: draftStore.StoreType,
        expiryDate: draftStore.ExpiryDate,
        expiryTime: draftStore.ExpiryTime || "23:59",
        eventType: draftStore.EventType,
        currency: draftStore.Currency,
        calculateTaxes: draftStore.CalculateTaxes,
        shopLogoUrl: draftStore.ShopLogoUrl,
        AttachedCustomerId: draftStore.attachedCustomers?.[0] || null,
        storeAddress: selectedStoreAddress,
        billingAddress: selectedStoreAddress,
        shippingAddress: selectedStoreAddress,
        homepageBannerUrl: draftStore.HomepageBannerUrl,
        primaryColor: draftStore.PrimaryColor,
        secondaryColor: draftStore.SecondaryColor,
        templateConfig: overviewTemplateConfig,
        assigned_to: toMinAssignedUsers(assignedUsers),
      };

      const response = await axios.put(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/edit-promostore/${id}`,
        payload
      );

      if (response.data?.promostore) {
        const normalizedStore = normalizePromoStoreRecord(response.data.promostore);
        latestStoreRef.current = normalizedStore;
        setStore(normalizedStore);
      } else {
        await fetchPromostore();
      }

      alert("Overview updated!");
    } catch (err) {
      console.error(err);
      alert(err?.response?.data?.message || "Failed to save overview.");
    }
  }, [assignedUsers, checkoutAddressSettings, clientAddresses, fetchPromostore, id, store, storeAddressId]);

  // -----------------------------
  // URL (memoized)
  // -----------------------------
  const fullStoreUrl = useMemo(() => buildPromoStorePublicUrl(store), [store]);
  const shippingSettings = useMemo(() => getPromoStoreShippingSettings(store), [store]);
  const paymentSettings = useMemo(() => getPromoStorePaymentSettings(store), [store]);
  const quotePurchaseOrderSettings = useMemo(() => getPromoStoreQuotePurchaseOrderSettings(store), [store]);
  const emailNotifications = useMemo(() => getPromoStoreEmailNotifications(store), [store]);
  const createdByUser = store?.createdByUser || store?.TemplateConfig?.createdByUser || null;
  const createdByName = getPromoStoreUserDisplayName(createdByUser);


  const requiredTaxCsvRegions = useMemo(() => {
    if (taxRegionDraft === "canada") return ["canada"];
    if (taxRegionDraft === "united_states") return ["united_states"];
    return ["canada", "united_states"];
  }, [taxRegionDraft]);

  const taxRegionLabel = useMemo(() => {
    if (taxRegionDraft === "canada") return "Canada";
    if (taxRegionDraft === "united_states") return "United States";
    return "Canada and United States";
  }, [taxRegionDraft]);

  const getTaxRegionLabel = useCallback((region) => {
    if (region === "canada") return "Canada";
    if (region === "united_states") return "United States";
    return "Canada and United States";
  }, []);

  const getTaxMetadataRegionKey = useCallback((region) => (
    region === "united_states" ? "unitedStates" : region
  ), []);

  const taxLastApplied = useMemo(() => (
    store?.TemplateConfig?.taxConfiguration?.lastApplied || null
  ), [store?.TemplateConfig]);

  const taxPendingConfiguration = useMemo(() => ({
    canada: normalizePendingTaxEntry(getPromoStorePendingTaxConfiguration(store)?.canada, "canada"),
    united_states: normalizePendingTaxEntry(getPromoStorePendingTaxConfiguration(store)?.unitedStates, "united_states"),
  }), [store?.TemplateConfig]);

  const taxRestoreSnapshots = useMemo(() => (
    store?.TemplateConfig?.taxConfiguration?.restoreSnapshots || {}
  ), [store?.TemplateConfig]);

  useEffect(() => {
    setTaxCsvFiles((prev) => ({
      canada: prev?.canada instanceof File ? prev.canada : (taxPendingConfiguration.canada ? {
        name: taxPendingConfiguration.canada.filename,
        size: taxPendingConfiguration.canada.size,
        persisted: true,
      } : null),
      united_states: prev?.united_states instanceof File ? prev.united_states : (taxPendingConfiguration.united_states ? {
        name: taxPendingConfiguration.united_states.filename,
        size: taxPendingConfiguration.united_states.size,
        persisted: true,
      } : null),
    }));
    setTaxCsvValidation((prev) => ({
      canada: prev?.canada && !prev.canada?.persisted ? prev.canada : buildPendingTaxValidation(taxPendingConfiguration.canada, "canada"),
      united_states: prev?.united_states && !prev.united_states?.persisted ? prev.united_states : buildPendingTaxValidation(taxPendingConfiguration.united_states, "united_states"),
    }));
  }, [taxPendingConfiguration]);

  const fetchTaxHistory = useCallback(async (options = {}) => {
    if (!id) return;
    setTaxHistoryError("");
    try {
      const params = {
        page: options.page || 1,
        pageSize: options.pageSize || 1,
      };
      const response = await axios.get(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/tax-rates/history`,
        { headers: { accessToken: localStorage.getItem("accessToken") }, params }
      );
      setTaxHistory({
        items: response.data?.items || [],
        pagination: response.data?.pagination || { page: params.page, pageSize: params.pageSize, total: 0, totalPages: 0 },
      });
    } catch (error) {
      setTaxHistoryError(error?.response?.data?.message || "Unable to load tax import history.");
    }
  }, [id]);

  useEffect(() => {
    fetchTaxHistory({ page: 1, pageSize: 1 });
  }, [fetchTaxHistory]);

  const visibleTaxCsvPanels = useMemo(() => [
    { key: "canada", label: "Canada" },
    { key: "united_states", label: "United States" },
  ].filter(({ key }) => requiredTaxCsvRegions.includes(key)), [requiredTaxCsvRegions]);

  const changedTaxCsvRegions = useMemo(() => (
    requiredTaxCsvRegions.filter((region) => taxCsvFiles[region] && taxCsvValidation[region]?.valid && taxCsvValidation[region]?.alreadyActive !== true)
  ), [requiredTaxCsvRegions, taxCsvFiles, taxCsvValidation]);

  const allRequiredTaxCsvValid = useMemo(() => (
    requiredTaxCsvRegions.every((region) => taxCsvFiles[region] && taxCsvValidation[region]?.valid)
  ), [requiredTaxCsvRegions, taxCsvFiles, taxCsvValidation]);

  const taxApplyButtonLabel = useMemo(() => {
    if (taxCsvWorking.applying) return "Applying Tax Rates...";
    if (allRequiredTaxCsvValid && requiredTaxCsvRegions.every((region) => taxCsvValidation[region]?.alreadyActive === true)) {
      return "No Tax Changes to Apply";
    }
    if (taxRegionDraft === "both") {
      if (allRequiredTaxCsvValid && changedTaxCsvRegions.length === 1) return changedTaxCsvRegions[0] === "canada" ? "Apply Canada Tax Rates" : "Apply United States Tax Rates";
      return "Apply Canada & United States Tax Rates";
    }
    if (taxRegionDraft === "canada") return "Apply Canada Tax Rates";
    if (taxRegionDraft === "united_states") return "Apply United States Tax Rates";
    return "Apply Canada & United States Tax Rates";
  }, [allRequiredTaxCsvValid, changedTaxCsvRegions, requiredTaxCsvRegions, taxCsvValidation, taxCsvWorking.applying, taxRegionDraft]);

  const canRestoreTaxRates = useMemo(() => (
    requiredTaxCsvRegions.every((region) => {
      const key = getTaxMetadataRegionKey(region);
      const snapshot = taxRestoreSnapshots?.[key];
      return snapshot?.version === 1 && Array.isArray(snapshot.rates);
    })
  ), [getTaxMetadataRegionKey, requiredTaxCsvRegions, taxRestoreSnapshots]);

  const taxRestoreButtonLabel = useMemo(() => {
    if (taxRestoreWorking) return "Restoring Tax Rates...";
    if (taxRegionDraft === "canada") return "Restore Previous Canada Tax Rates";
    if (taxRegionDraft === "united_states") return "Restore Previous United States Tax Rates";
    return "Restore Previous Canada & United States Tax Rates";
  }, [taxRegionDraft, taxRestoreWorking]);

  const taxReadinessMessage = useMemo(() => {
    const canadaFile = taxCsvFiles.canada;
    const usFile = taxCsvFiles.united_states;
    const canadaValidation = taxCsvValidation.canada;
    const usValidation = taxCsvValidation.united_states;
    const canadaValid = !!canadaFile && canadaValidation?.valid;
    const usValid = !!usFile && usValidation?.valid;
    const canadaActive = canadaValid && canadaValidation?.alreadyActive === true;
    const usActive = usValid && usValidation?.alreadyActive === true;
    const canadaChanged = canadaValid && !canadaActive;
    const usChanged = usValid && !usActive;
    const canadaInvalid = !!canadaFile && canadaValidation && !canadaValidation.valid;
    const usInvalid = !!usFile && usValidation && !usValidation.valid;

    if (taxRegionDraft === "canada") {
      if (!canadaFile) return "Upload and validate a Canada tax CSV before applying.";
      if (canadaInvalid) return "Fix the Canada CSV validation errors before applying.";
      if (canadaActive) return "Canada tax file is already active. No changes detected.";
      if (canadaChanged) return "Canada tax file validated. Ready to apply.";
      return "Upload and validate a Canada tax CSV before applying.";
    }

    if (taxRegionDraft === "united_states") {
      if (!usFile) return "Upload and validate a United States tax CSV before applying.";
      if (usInvalid) return "Fix the United States CSV validation errors before applying.";
      if (usActive) return "United States tax file is already active. No changes detected.";
      if (usChanged) return "United States tax file validated. Ready to apply.";
      return "Upload and validate a United States tax CSV before applying.";
    }

    if (canadaInvalid || usInvalid) return "Fix the remaining CSV validation errors before applying.";
    if (canadaActive && usActive) return "Both tax files are already active. No changes detected.";
    if (canadaActive && usChanged) return "United States tax changes are ready to apply. Canada is unchanged.";
    if (usActive && canadaChanged) return "Canada tax changes are ready to apply. United States is unchanged.";
    if (canadaChanged && usChanged) return "Both tax files are validated and ready to apply.";
    if (canadaValid && !usValid) return "Canada file validated. Upload and validate the United States file to continue.";
    if (usValid && !canadaValid) return "United States file validated. Upload and validate the Canada file to continue.";
    return "Upload and validate both tax CSV files before applying.";
  }, [taxCsvFiles, taxCsvValidation, taxRegionDraft]);

  const taxDashboardRegions = useMemo(() => ["canada", "united_states"].map((region) => {
    const key = getTaxMetadataRegionKey(region);
    const metadata = taxLastApplied?.[key];
    const snapshot = taxRestoreSnapshots?.[key];
    const pending = taxPendingConfiguration[region];
    const isActive = metadata?.status === "active" || metadata?.status === "restored";
    const isWarning = taxLastApplied?.status === "partial_success" || metadata?.status === "warning";
    const isPending = !isActive && !!pending?.valid;
    return {
      region,
      key,
      label: getTaxRegionLabel(region),
      status: isWarning ? "Warning" : isActive ? "Active" : isPending ? "Pending" : "Not configured",
      rowCount: metadata?.rowCount ?? pending?.rowCount,
      appliedAt: metadata?.appliedAt,
      appliedByName: metadata?.appliedByName,
      filename: metadata?.filename ?? pending?.filename,
      restoreAvailable: snapshot?.version === 1 && Array.isArray(snapshot.rates),
    };
  }), [getTaxMetadataRegionKey, getTaxRegionLabel, taxLastApplied, taxPendingConfiguration, taxRestoreSnapshots]);

  const taxOverallStatus = useMemo(() => {
    const activeCount = taxDashboardRegions.filter((region) => region.status === "Active").length;
    if (taxDashboardRegions.some((region) => region.status === "Warning")) return "Needs attention";
    if (activeCount === 0) return "Not configured";
    if (activeCount === taxDashboardRegions.length) return "Healthy";
    return "Partially configured";
  }, [taxDashboardRegions]);

  const latestTaxHistoryItem = taxHistory.items?.[0] || null;

  const renderTaxResultCounts = useCallback((result) => {
    if (!result) return null;
    return (
      <div className="promostore-tax-result-counts">
        {result.submitted !== undefined && <div>Submitted: {result.submitted}</div>}
        {result.inserted !== undefined && <div>Inserted: {result.inserted}</div>}
        {result.removed !== undefined && <div>Removed: {result.removed}</div>}
        {result.unchanged !== undefined && <div>Unchanged: {result.unchanged}</div>}
        {result.restored !== undefined && <div>Restored rows: {result.restored}</div>}
        {result.locationsRestored !== undefined && <div>Location rows restored: {result.locationsRestored}</div>}
      </div>
    );
  }, []);

  const getTaxImportedCount = useCallback((results) => (
    Object.values(results || {}).reduce((sum, result) => (
      sum + (Number(result?.submitted ?? result?.inserted ?? 0) || 0)
    ), 0)
  ), []);

  const canApplyTaxCsv = useMemo(() => (
    allRequiredTaxCsvValid
    && changedTaxCsvRegions.length > 0
    && !taxCsvWorking.applying
    && !taxRestoreWorking
    && !requiredTaxCsvRegions.some((region) => taxCsvWorking[region])
  ), [allRequiredTaxCsvValid, changedTaxCsvRegions.length, requiredTaxCsvRegions, taxCsvWorking, taxRestoreWorking]);

  const formatTaxCsvSize = useCallback((file) => {
    if (!file) return "";
    if (file.size < 1024) return `${file.size} B`;
    if (file.size < 1024 * 1024) return `${(file.size / 1024).toFixed(1)} KB`;
    return `${(file.size / (1024 * 1024)).toFixed(2)} MB`;
  }, []);

  const validateTaxCsvFile = useCallback(async (region, file) => {
    if (!file) return;
    setTaxCsvWorking((prev) => ({ ...prev, [region]: true }));
    setTaxCsvStatus(null);
    setTaxSuccessDetailsExpanded(false);
    const formData = new FormData();
    formData.append("region", region);
    formData.append("file", file);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/tax-rates/validate`,
        formData,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      const validation = response.data?.validation || null;
      setTaxCsvValidation((prev) => ({ ...prev, [region]: validation }));
      if (validation?.valid) {
        const csv = await file.text();
        setStore((prev) => {
          const regionKey = getTaxPendingRegionKey(region);
          const next = {
            ...prev,
            TemplateConfig: mergePromoStoreTemplateConfig(prev, {
              taxConfiguration: {
                ...(getPromoStoreTemplateConfig(prev)?.taxConfiguration || {}),
                pending: {
                  ...getPromoStorePendingTaxConfiguration(prev),
                  [regionKey]: {
                    region,
                    filename: validation.filename || file.name,
                    rowCount: validation.rowCount || 0,
                    fileHash: validation.fileHash || "",
                    size: file.size || 0,
                    valid: true,
                    csv,
                    detectedStates: Array.isArray(validation.detectedStates) ? validation.detectedStates : [],
                    duplicateCount: validation.duplicateCount || 0,
                    alreadyActive: validation.alreadyActive === true,
                    warnings: Array.isArray(validation.warnings) ? validation.warnings : [],
                    errors: [],
                  },
                },
              },
            }),
          };
          latestStoreRef.current = next;
          return next;
        });
      }
    } catch (error) {
      setTaxCsvValidation((prev) => ({
        ...prev,
        [region]: error?.response?.data?.validation || {
          valid: false,
          region,
          filename: file.name,
          rowCount: 0,
          errors: [{ message: error?.response?.data?.message || "Failed to validate CSV." }],
          warnings: [],
          detectedStates: [],
          duplicateCount: 0,
        },
      }));
    } finally {
      setTaxCsvWorking((prev) => ({ ...prev, [region]: false }));
    }
  }, [id]);

  const handleTaxCsvSelect = useCallback((region, file) => {
    if (!file) return;
    setTaxCsvFiles((prev) => ({ ...prev, [region]: file }));
    setTaxCsvValidation((prev) => ({ ...prev, [region]: null }));
    setTaxExpandedErrors((prev) => ({ ...prev, [region]: false }));
    setTaxExpandedRegions((prev) => ({ ...prev, [region]: false }));
    validateTaxCsvFile(region, file);
  }, [validateTaxCsvFile]);

  const handleTaxCsvRemove = useCallback((region) => {
    setTaxCsvFiles((prev) => ({ ...prev, [region]: null }));
    setTaxCsvValidation((prev) => ({ ...prev, [region]: null }));
    setTaxExpandedErrors((prev) => ({ ...prev, [region]: false }));
    setTaxExpandedRegions((prev) => ({ ...prev, [region]: false }));
    setTaxCsvStatus(null);
    setStore((prev) => {
      const regionKey = getTaxPendingRegionKey(region);
      const next = {
        ...prev,
        TemplateConfig: mergePromoStoreTemplateConfig(prev, {
          taxConfiguration: {
            ...(getPromoStoreTemplateConfig(prev)?.taxConfiguration || {}),
            pending: {
              ...getPromoStorePendingTaxConfiguration(prev),
              [regionKey]: null,
            },
          },
        }),
      };
      latestStoreRef.current = next;
      return next;
    });
  }, []);

  const handleTaxRegionChange = useCallback((value) => {
    setTaxRegionDraft(value);
    setExpandedTaxCountry(value === "united_states" ? "united_states" : "canada");
    setTaxCsvStatus(null);
  }, []);

  const handleApplyTaxCsv = useCallback(async () => {
    if (!canApplyTaxCsv) return;
    setTaxCsvWorking((prev) => ({ ...prev, applying: true }));
    setTaxCsvStatus(null);
    const formData = new FormData();
    formData.append("region", taxRegionDraft);
    requiredTaxCsvRegions.forEach((region) => {
      const file = taxCsvFiles[region];
      if (file instanceof File) {
        formData.append(region, file);
      }
    });

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/tax-rates/apply`,
        formData,
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setTaxSuccessDetailsExpanded(false);
      setTaxCsvStatus({
        type: "success",
        message: response.data?.message || "Tax rates were applied to WordPress successfully.",
        warning: !!response.data?.warning,
        noChanges: !!response.data?.noChanges,
        changedRegions: response.data?.changedRegions || [],
        unchangedRegions: response.data?.unchangedRegions || [],
        region: response.data?.region || taxRegionDraft,
        results: response.data?.results || null,
        appliedAt: response.data?.appliedAt || response.data?.lastApplied?.appliedAt || new Date().toISOString(),
        appliedBy: response.data?.appliedBy?.name || response.data?.lastApplied?.appliedByName || null,
      });
      if (response.data?.lastApplied) {
        setStore((prev) => {
          const pending = { ...getPromoStorePendingTaxConfiguration(prev) };
          (response.data?.changedRegions || []).forEach((region) => {
            pending[getTaxPendingRegionKey(region)] = null;
          });
          const next = {
            ...prev,
            TemplateConfig: mergePromoStoreTemplateConfig(prev, {
              taxConfiguration: {
                ...(getPromoStoreTemplateConfig(prev)?.taxConfiguration || {}),
                lastApplied: response.data.lastApplied,
                pending,
              },
            }),
          };
          latestStoreRef.current = next;
          return next;
        });
      }
      setTaxCsvFiles((prev) => {
        const next = { ...prev };
        (response.data?.changedRegions || []).forEach((region) => {
          next[region] = null;
        });
        return next;
      });
      setTaxCsvValidation((prev) => {
        const next = { ...prev };
        (response.data?.changedRegions || []).forEach((region) => {
          next[region] = null;
        });
        return next;
      });
        setTaxApplyConfirmOpen(false);
      fetchTaxHistory({ page: 1 });
    } catch (error) {
      if (error?.response?.data?.validations) {
        setTaxCsvValidation((prev) => ({ ...prev, ...error.response.data.validations }));
      }
      setTaxCsvStatus({
        type: "error",
        message: error?.response?.data?.message || "Failed to apply tax rates to WordPress.",
        rollbackRestored: error?.response?.data?.rollbackRestored === true,
      });
      setTaxApplyConfirmOpen(false);
    } finally {
      setTaxCsvWorking((prev) => ({ ...prev, applying: false }));
    }
  }, [canApplyTaxCsv, fetchTaxHistory, id, requiredTaxCsvRegions, taxCsvFiles, taxRegionDraft]);

  const handleRestoreTaxRates = useCallback(async () => {
    if (!canRestoreTaxRates || taxRestoreWorking) return;
    setTaxRestoreWorking(true);
    setTaxCsvStatus(null);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/tax-rates/restore`,
        { region: taxRegionDraft },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );
      setTaxSuccessDetailsExpanded(false);
      setTaxCsvStatus({
        type: "success",
        action: "restore",
        message: response.data?.message || "Previous tax rates restored successfully.",
        region: response.data?.region || taxRegionDraft,
        results: response.data?.results || null,
        restoredAt: response.data?.restoredAt || new Date().toISOString(),
        restoredBy: response.data?.restoredBy?.name || null,
      });
      setStore((prev) => {
        const next = {
          ...prev,
          TemplateConfig: {
            ...(prev?.TemplateConfig || {}),
            taxConfiguration: {
              ...(prev?.TemplateConfig?.taxConfiguration || {}),
              lastApplied: response.data?.lastApplied || prev?.TemplateConfig?.taxConfiguration?.lastApplied,
              restoreSnapshots: {
                ...(prev?.TemplateConfig?.taxConfiguration?.restoreSnapshots || {}),
                ...(response.data?.restoreSnapshots || {}),
              },
            },
          },
        };
        latestStoreRef.current = next;
        return next;
      });
        setTaxRestoreConfirmOpen(false);
      fetchTaxHistory({ page: 1 });
    } catch (error) {
      setTaxCsvStatus({
        type: "error",
        action: "restore",
        message: error?.response?.data?.message || "Unable to restore previous tax rates.",
        rollbackRestored: error?.response?.data?.rollbackRestored === true,
        region: taxRegionDraft,
      });
      setTaxRestoreConfirmOpen(false);
    } finally {
      setTaxRestoreWorking(false);
    }
  }, [canRestoreTaxRates, fetchTaxHistory, id, taxRegionDraft, taxRestoreWorking]);

  const getTaxHistoryActionLabel = useCallback((action) => ({
    tax_apply: "Tax rates applied",
    tax_apply_no_changes: "No tax changes applied",
    tax_apply_failed: "Tax apply failed",
    tax_restore: "Previous tax rates restored",
    tax_restore_failed: "Tax restore failed",
    tax_preview: "Tax changes previewed",
    tax_download: "Tax rates downloaded",
  }[action] || action), []);

  const getTaxHistoryStatusLabel = useCallback((status) => ({
    success: "Successful",
    failed: "Failed",
    no_changes: "No changes",
    partial_success: "Partial success",
    rolled_back: "Rolled back",
  }[status] || status), []);

  const emailNotificationRows = useMemo(
    () => [
      { key: "newOrder", label: "New order" },
      { key: "failedOrder", label: "Failed order" },
      { key: "cancelledOrder", label: "Cancelled order" },
    ],
    []
  );

  // -----------------------------
  // Request developer
  // -----------------------------
  const handleRequestDeveloper = useCallback(async () => {
    if (!store || !store.id) {
      alert("Store details not loaded yet.");
      return;
    }

    setRequestDeveloperOpen(true);
  }, [id, instanceUniqueId, store]);

  const handleConfirmRequestDeveloper = useCallback(async () => {
    if (!store || !store.id) {
      alert("Store details not loaded yet.");
      return;
    }

    setRequestingDev(true);
    try {
      await axios.post(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/request-developer`,
        {
          storeId: store.id,
          instanceUniqueId,
          shopName: store.ShopName,
          storeType: store.StoreType,
        },
        { headers: { accessToken: localStorage.getItem("accessToken") } }
      );

      alert("Developer request sent to the PromoStore team.");
      setRequestDeveloperOpen(false);
    } catch (err) {
      console.error(err);
      alert(
        "Could not send the developer request automatically. Please email info@elitedm.ca directly."
      );
    } finally {
      setRequestingDev(false);
    }
  }, [id, instanceUniqueId, store]);

  useEffect(() => {
    const fetchPromoStoreSettings = async () => {
      if (!instanceUniqueId) return;
      try {
        const instanceRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/byinstanceUniqueId/${instanceUniqueId}`
        );
        const instanceId = instanceRes.data?.id;
        if (!instanceId) return;

        const settingsRes = await axios.get(
          `${process.env.REACT_APP_NODEJS_SERVER_URL}/instances/${instanceId}/promostore-settings`,
          { headers: { accessToken: localStorage.getItem("accessToken") } }
        );

        setPromoStoreSettings((prev) => ({
          ...prev,
          ...(settingsRes.data?.settings || {}),
        }));
        setDeveloperTeamUsers(settingsRes.data?.teamUsers || []);
      } catch (error) {
        console.error("Failed to load PromoStore settings:", error);
      }
    };

    fetchPromoStoreSettings();
  }, [instanceUniqueId]);

  const promoStoreHeaderIdentity = useMemo(() => buildPromoStoreHeaderIdentity(store), [store]);

  return (
    <div className="instance-dashboard instance-webstores-page">
      <InstanceLeftSidebar />
      <div className="rightSide">
        <div className="options-container fade-in-effect-usability">
          <div className="options-inner-container" style={{ position: "relative" }}>
            {/* Top-right URL + Request Developer */}
            {fullStoreUrl && (
              <Box
                sx={{
                  position: "absolute",
                  top: 50,
                  right: 30,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  zIndex: 2,
                }}
              >
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleRequestDeveloper}
                  disabled={requestingDev}
                  sx={{
                    textTransform: "none",
                    fontWeight: 600,
                    borderRadius: 999,
                    backgroundColor: "#222",
                    "&:hover": { backgroundColor: "#000" },
                    px: 2,
                  }}
                >
                  {requestingDev
                    ? "Requesting..."
                    : promoStoreSettings.requestDeveloperLabel || "Request developer"}
                </Button>

                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    maxWidth: 340,
                    px: 1.5,
                    py: 0.5,
                    backgroundColor: "#ffffff",
                    borderRadius: 999,
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    border: "1px solid #eee",
                    cursor: "pointer",
                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.14)" },
                  }}
                  onClick={() => window.open(fullStoreUrl, "_blank")}
                >
                  <LaunchIcon fontSize="small" sx={{ opacity: 0.8 }} />
                  <Typography
                    variant="body2"
                    sx={{
                      maxWidth: 280,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                    title={fullStoreUrl}
                  >
                    {fullStoreUrl}
                  </Typography>
                </Box>
              </Box>
            )}

            {!loading && (
            <PageHeaderWithBreadcrumbs
              title={(
                <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
                  <Avatar
                    component="span"
                    src={promoStoreHeaderIdentity.logoUrl || undefined}
                    alt={promoStoreHeaderIdentity.name || "PromoStore"}
                    sx={{
                      width: 42,
                      height: 42,
                      bgcolor: "#f8fafc",
                      color: "#0f172a",
                      border: "1px solid #e2e8f0",
                      fontSize: "0.95rem",
                      fontWeight: 800,
                    }}
                  >
                    {initialsFromName(promoStoreHeaderIdentity.name)}
                  </Avatar>
                  <Box component="span" sx={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {promoStoreHeaderIdentity.name || store.ShopName || `Store #${id}`}
                  </Box>
                </Box>
              )}
              breadcrumb={store.ShopName ? store.ShopName : "Store #" + id}
              includeButton={false}
            />
            )}

            <Dialog
              open={Boolean(approvedListDialogState)}
              onClose={closeApprovedListDialog}
              maxWidth="sm"
              fullWidth
              className="promostore-management-dialog"
            >
              <DialogTitle>{approvedListDialogState?.title || "Edit Approved List"}</DialogTitle>
              <DialogContent dividers>
                <div className="promostore-dialog-stack">
                  {availableApprovedListCandidates.length ? (
                    <div className="promostore-dialog-inline-actions">
                      <div className="promostore-dialog-inline-field">
                        <label htmlFor="promostore-approved-list-selection">Select from client</label>
                        <select
                          id="promostore-approved-list-selection"
                          value={approvedListCandidateId}
                          onChange={(e) => setApprovedListCandidateId(e.target.value)}
                        >
                          <option value="">Select an address...</option>
                          {availableApprovedListCandidates.map((option) => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addApprovedListDraftId}
                        disabled={!approvedListCandidateId}
                      >
                        Add
                      </Button>
                    </div>
                  ) : null}

                  {currentApprovedListOptions.length ? (
                    <div className="promostore-contact-management-list">
                      <div className="promostore-contact-management-header">
                        <span>Approved Address</span>
                        <span>Actions</span>
                      </div>
                      {currentApprovedListOptions.map((option) => (
                        <div key={option.id} className="promostore-contact-management-row">
                          <div className="promostore-contact-management-name">
                            <strong>{option.label}</strong>
                          </div>
                          <div className="promostore-contact-management-actions">
                            <Button size="small" color="error" onClick={() => removeApprovedListDraftId(option.id)}>
                              Remove
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {approvedListDialogState?.emptyMessage || "No approved items are available."}
                    </Typography>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeApprovedListDialog}>Cancel</Button>
                <Button variant="contained" onClick={handleApprovedListDialogSave}>Done</Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={Boolean(contactManagementDialogState)}
              onClose={closeContactManagementDialog}
              maxWidth="sm"
              fullWidth
              className="promostore-management-dialog"
            >
              <DialogTitle>{contactManagementDialogState?.title || "Manage Contacts"}</DialogTitle>
              <DialogContent dividers>
                <div className="promostore-dialog-stack">
                  {availableManagedContactCandidates.length ? (
                    <div className="promostore-dialog-inline-actions">
                      <div className="promostore-dialog-inline-field">
                        <label htmlFor="promostore-managed-contact-selection">Select from client</label>
                        <select
                          id="promostore-managed-contact-selection"
                          value={contactManagementCandidateId}
                          onChange={(e) => setContactManagementCandidateId(e.target.value)}
                        >
                          <option value="">Select a contact...</option>
                          {availableManagedContactCandidates.map((option) => (
                            <option key={option.id} value={option.id}>{option.label}</option>
                          ))}
                        </select>
                      </div>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={addContactManagementDraftId}
                        disabled={!contactManagementCandidateId}
                      >
                        Add
                      </Button>
                    </div>
                  ) : null}

                  {currentManagedContactOptions.length ? (
                    <div className="promostore-contact-management-list">
                      <div className="promostore-contact-management-header">
                        <span>Contact Name</span>
                        <span>Actions</span>
                      </div>
                      {currentManagedContactOptions.map((option) => (
                        <div key={option.id} className="promostore-contact-management-row">
                          <div className="promostore-contact-management-name">
                            <strong>{option.label}</strong>
                            {option.contact?.email && <small>{option.contact.email}</small>}
                          </div>
                          <div className="promostore-contact-management-actions">
                            <Button
                              size="small"
                              color="error"
                              onClick={() => handleManagedContactRemove(option)}
                            >
                              Remove from list
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      {contactManagementDialogState?.emptyMessage || "No contacts are available for this customer."}
                    </Typography>
                  )}
                </div>
              </DialogContent>
              <DialogActions>
                <Button onClick={closeContactManagementDialog}>Cancel</Button>
                <Button
                  variant="contained"
                  onClick={handleContactManagementDialogSave}
                >
                  Done
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog
              open={requestDeveloperOpen}
              onClose={() => setRequestDeveloperOpen(false)}
              maxWidth="sm"
              fullWidth
            >
              <DialogTitle>Request PromoStore developer</DialogTitle>
              <DialogContent dividers>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  You are about to notify the following developers:
                </Typography>
                {developerTeamUsers.length ? (
                  <List dense>
                    {developerTeamUsers.map((user) => (
                      <ListItem key={user.id}>
                        <ListItemText
                          primary={`${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email}
                          secondary={user.email}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No PromoStore developers have been configured in Company Settings.
                  </Typography>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setRequestDeveloperOpen(false)} disabled={requestingDev}>
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmRequestDeveloper}
                  disabled={requestingDev || developerTeamUsers.length === 0}
                >
                  {requestingDev ? "Sending..." : "Confirm request"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={syncDialogOpen} onClose={() => !syncing && setSyncDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Initiate Website Sync</DialogTitle>
              <DialogContent>
                <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
                  What type of sync would you like to run?
                </Typography>
                <FormControl fullWidth size="small" sx={{ mb: 1.5 }}>
                  <Select
                    value={syncDirection}
                    onChange={(event) => setSyncDirection(event.target.value)}
                    disabled={syncing}
                  >
                    <MenuItem value="core_to_wordpress">Core to WordPress (push Elite Core changes)</MenuItem>
                    <MenuItem value="wordpress_to_core" disabled={!canRunWordpressToCoreSync}>
                      WordPress to Core (pull website settings)
                    </MenuItem>
                  </Select>
                </FormControl>

                {!canRunWordpressToCoreSync && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Only users with the <strong>administrator</strong> role can run WordPress to Core sync.
                  </Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setSyncDialogOpen(false)} disabled={syncing}>
                  Cancel
                </Button>
                <Button variant="contained" onClick={runSync} disabled={syncing}>
                  {syncing ? "Syncing..." : "Confirm Sync"}
                </Button>
              </DialogActions>
            </Dialog>

            <Dialog open={archiveDialogOpen} onClose={() => !archiveWorking && setArchiveDialogOpen(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Delete / archive PromoStore</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This archives the Elite Core PromoStore record. It does not hard-delete products, orders, files, chats, comments, assignments, settings, or history.
                </Alert>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Type <strong>DELETE</strong> or <strong>{archiveConfirmTarget}</strong> to confirm.
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={archiveConfirmText}
                  onChange={(e) => setArchiveConfirmText(e.target.value)}
                  disabled={archiveWorking}
                />
                {archiveMessage && (
                  <Alert severity={archiveMessage.severity} sx={{ mt: 2 }}>{archiveMessage.text}</Alert>
                )}
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setArchiveDialogOpen(false)} disabled={archiveWorking}>Cancel</Button>
                <Button color="error" variant="contained" onClick={handleArchivePromoStore} disabled={archiveWorking || !canConfirmArchive}>
                  {archiveWorking ? "Archiving..." : "Archive PromoStore"}
                </Button>
              </DialogActions>
            </Dialog>

            {archiveMessage && !archiveDialogOpen && (
              <Alert severity={archiveMessage.severity} sx={{ mb: 2 }}>{archiveMessage.text}</Alert>
            )}

            {loading ? (
              <Box className="promostore-details-page-loader">
                <CircularProgress />
              </Box>
            ) : (
              store && (
                <>
                  <div className="order-container">
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 2,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        className="promostore-tabs-shell"
                        sx={{
                          borderBottom: 1,
                          borderColor: "divider",
                          bgcolor: "background.paper",
                          display: "flex",
                          alignItems: "center",
                                                        borderRadius: 10,

                          flex: 1,
                          minWidth: 0,
                        }}
                      >
                        <Tabs
                          value={PROMOSTORE_TABS.some((tab) => tab.label === activeTab) ? activeTab : "Overview"}
                          onChange={(_, value) => handleTabChange(value)}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{
                            flex: 1,
                            minHeight: 44,
                            borderRadius: 10,
                            "& .MuiTab-root": {
                              minHeight: 44,
                              fontSize: "0.8125rem",
                              fontWeight: 600,
                              textTransform: "none",
                              letterSpacing: 0,
                              color: "text.secondary",
                              borderRadius: 10,
                              opacity: 1,
                              py: 0,
                              px: 1.75,
                              gap: 0.625,
                            },
                            "& .Mui-selected": {
                              color: "text.primary",
                              fontWeight: 700,
                            },
                            "& .MuiTabs-indicator": {
                              height: 2.5,
                              bgcolor: "#c3171d",
                              borderRadius: "2px 2px 0 0",
                            },
                          }}
                        >
                          {PROMOSTORE_TABS.map(({ label, Icon }) => (
                            <Tab
                              key={label}
                              value={label}
                              label={label}
                              icon={<Icon sx={{ fontSize: 17 }} />}
                              iconPosition="start"
                            />
                          ))}
                        </Tabs>
                      </Box>

                        <div className="dropdown-container" style={{ position: "relative" }}>
                          <Box
                            className="introduction-field"
                            sx={{ display: "flex", justifyContent: "center", marginRight: "1rem" }}
                          >
                            <Button
                              variant="contained"
                              onClick={handleActionsClick}
                              sx={{
                                textTransform: "none",
                                color: "white",
                                backgroundColor: "#c3171d",
                                "&:hover": { backgroundColor: "white", color: "#c3171d" },
                                width: "200px",
                                fontWeight: 600,
                                display: "flex",
                                alignItems: "center",
                                gap: "0.5rem",
                              }}
                            >
                              Actions
                            </Button>
                          </Box>

                          {showActions && (
                            <Box
                              className="dropdown-menu"
                              sx={{
                                position: "absolute",
                                top: "50px",
                                left: 0,
                                backgroundColor: "#fff",
                                boxShadow: 3,
                                padding: "10px",
                                zIndex: 10,
                              }}
                            >
                              <Button
                                onClick={() => window.open(fullStoreUrl, "_blank")}
                                sx={{ width: "100%", marginBottom: "10px", fontWeight: 700 }}
                              >
                                View Site
                              </Button>

                              <Button
                                onClick={handleInitiateSync}
                                disabled={syncing}
                                sx={{ width: "100%", fontWeight: 700, marginBottom: "10px" }}
                              >
                                {syncing ? "Syncing..." : "Initiate website sync"}
                              </Button>

                              {store.archived ? (
                                <Button
                                  onClick={handleRestorePromoStore}
                                  disabled={archiveWorking}
                                  sx={{ width: "100%", marginBottom: "10px", fontWeight: 700 }}
                                >
                                  Restore PromoStore
                                </Button>
                              ) : (
                                <Button
                                  onClick={() => {
                                    if (!isAdministrator) {
                                      setArchiveMessage({ severity: "info", text: "Please contact an administrator to delete/archive this PromoStore." });
                                      return;
                                    }
                                    setArchiveDialogOpen(true);
                                  }}
                                  sx={{ width: "100%", marginBottom: "10px", fontWeight: 700 }}
                                >
                                  Delete Web Store
                                </Button>
                              )}
                            </Box>
                          )}
                        </div>
                    </Box>

                    <Box className="order-progress-bar" sx={{ width: "100%", mb: 0 }}>
                      <Box sx={{ display: "flex", width: "100%", borderBottom: "1px solid #ddd" }}>
                        <Box
                          sx={{
                            width: "100%",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            position: "relative",
                            padding: "12px 16px",
                          }}
                          className="chat-header-tab"
                          onClick={() => togglePanel("chat")}
                        >
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <ChatIcon sx={{ mr: 1 }} />
                            <Typography variant="h6"></Typography>
                          </Box>

                          <Box
                            sx={{
                              position: "absolute",
                              right: 16,
                              top: "50%",
                              transform: "translateY(-50%)",
                            }}
                          >
                            {expanded === "chat" ? <RemoveIcon /> : <AddIcon />}
                          </Box>
                        </Box>
                      </Box>

                      {expanded === "chat" && (
                        <Box className="panel chat-panel">
                          <ChatBox assignedUsers={assignedUsers} webStoreId={store.id} />
                        </Box>
                      )}
                    </Box>

                    <div className="order-details-section">

                      <div className="store-info order-details-section">
                        {/* ========== Design ========== */}
                        {activeTab === "Design" && (
                          <InstanceWebStoreDesign
                            store={store}
                            categories={promoStoreCategories}
                            onStoreChange={handleStoreChange}
                            onGenerateAiDesign={async ({ prompt, focus, selectedPages }) => {
                              const resp = await axios.post(
                                `${process.env.REACT_APP_NODEJS_SERVER_URL}/promostores/${id}/ai-generate-website`,
                                { prompt, focus, selectedPages, selectedServices: [] }
                              );

                              const data = resp.data;

                              // Update local store state so UI instantly shows preview without waiting for refresh
                              handleStoreChange("TemplateConfig", data.templateConfig);
                            }}
                            //onInitiateSync={onInitiateSync}
                            onGetWpPages={onGetWpPages}
                            onRefreshWpPages={onRefreshWpPages}                      
                            />
                        )}

                        {activeTab === "Overview" && (
                          <div className="promostore-overview order-container-sidebar overview-layout">
                            <div className="order-details-section overview-details-panel promostore-overview-main">
                              <h2>PromoStore Status</h2>
                              <form className="overview-details-form">
                                <div className="details-section-group">
                                  <div className="details-section-heading">Customer and assignment</div>
                                  <div className="order-info-fields">
                                    <div className="field">
                                      <label htmlFor="customerAccount">Customer Account</label>
                                      <MemoCustomerSearchSelectAsync
                                        showLabel={false}
                                        inputId="customerAccount"
                                        instanceUniqueId={instanceUniqueId}
                                        value={attachedCustomerId}
                                        onChange={handleCustomerChange}
                                      />
                                    </div>

                                    <div className="field">
                                      <label htmlFor="siteURL">Store URL</label>
                                      <input type="url" id="siteURL" value={fullStoreUrl} readOnly />
                                    </div>

                                    <div className="field">
                                      <label>Created By</label>
                                      <Box sx={{ display: "flex", alignItems: "center", gap: 1, minHeight: 40 }}>
                                        {createdByUser ? (
                                          <>
                                            <Avatar
                                              src={getPromoStoreUserAvatar(createdByUser)}
                                              alt={createdByName}
                                              sx={{ width: 32, height: 32 }}
                                            />
                                            <span>{createdByName}</span>
                                          </>
                                        ) : (
                                          <span>Unknown creator</span>
                                        )}
                                      </Box>
                                    </div>
                                  </div>

                                  <div className="promostore-assignment-block">
                                    <OrderUserAssignment
                                      orderId={store.promoStoreId}
                                      initialAssignedUsers={assignedUsers || []}
                                      availableUsers={availableUsers}
                                      onChangeAssignedUsers={handleAssignedUsersChange}
                                    />
                                  </div>
                                </div>

                                <div className="details-section-group">
                                  <div className="details-section-heading">Store details</div>
                                  <div className="order-info-fields">
                                    <div className="field">
                                      <label htmlFor="shopName">Shop Name</label>
                                      <input
                                        type="text"
                                        id="shopName"
                                        value={store.ShopName || store.title || ""}
                                        onChange={(e) => handleStoreChange("ShopName", e.target.value)}
                                      />
                                    </div>

                                    <div className="field">
                                      <label htmlFor="Phone">Shop Contact Phone</label>
                                      <input
                                        type="tel"
                                        id="Phone"
                                        value={store.Phone || ""}
                                        onChange={(e) => handleStoreChange("Phone", e.target.value)}
                                      />
                                    </div>

                                    <div className="field">
                                      <label htmlFor="status">
                                        Status
                                        <Tooltip title="Controls whether the store is accepting orders. 'Live' = customers can check out. 'Closed' = site may be viewable, but checkout should be disabled." arrow>
                                          <InfoOutlinedIcon fontSize="small" className="field-tooltip-icon" />
                                        </Tooltip>
                                      </label>
                                      <select
                                        id="status"
                                        value={store.status}
                                        onChange={(e) => handleStoreChange("status", e.target.value)}
                                      >
                                        <option value="live">Live</option>
                                        <option value="closed">Closed</option>
                                      </select>
                                    </div>

                                    <div className="field">
                                      <label htmlFor="StoreType">
                                        Store Type
                                        <Tooltip title="Defines how the store behaves. Company Store = ongoing, broader catalog. Pop-up Store = time-boxed, simpler template. Program = more complex, rules-driven store for recurring programs." arrow>
                                          <InfoOutlinedIcon fontSize="small" className="field-tooltip-icon" />
                                        </Tooltip>
                                      </label>
                                      <FormControl fullWidth size="small">
                                        <Select
                                          id="StoreType"
                                          value={normalizePromoStoreStoreType(store.StoreType)}
                                          onChange={(e) => handleStoreTypeChange(e.target.value)}
                                          displayEmpty
                                        >
                                          <MenuItem value="company_store">
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                              <BusinessCenterIcon fontSize="small" />
                                              <span>Company Store</span>
                                            </Box>
                                          </MenuItem>
                                          <MenuItem value="pop_up_store">
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                              <StorefrontIcon fontSize="small" />
                                              <span>Pop-up Store</span>
                                            </Box>
                                          </MenuItem>
                                          <MenuItem value="program_store">
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                              <AllInclusiveIcon fontSize="small" />
                                              <span>Program</span>
                                            </Box>
                                          </MenuItem>
                                        </Select>
                                      </FormControl>
                                    </div>

                                    <div className="field">
                                      <label htmlFor="ExpiryDate">
                                        Expiry Date
                                        <Tooltip title="Optional closing date for the store. After this date, checkout should be blocked. Leave blank for no automatic close." arrow>
                                          <InfoOutlinedIcon fontSize="small" className="field-tooltip-icon" />
                                        </Tooltip>
                                      </label>
                                      <input
                                        type="date"
                                        id="ExpiryDate"
                                        value={store.ExpiryDate ? store.ExpiryDate.slice(0, 10) : ""}
                                        onChange={(e) => handleStoreChange("ExpiryDate", e.target.value)}
                                      />
                                    </div>

                                    <div className="field">
                                      <label htmlFor="ExpiryTime">Closing Time</label>
                                      <input
                                        type="time"
                                        id="ExpiryTime"
                                        step="60"
                                        value={store.ExpiryTime || "23:59"}
                                        onChange={(e) => handleStoreChange("ExpiryTime", e.target.value)}
                                      />
                                      <small className="field-helper-text">Defaults to 11:59 PM in the company timezone.</small>
                                    </div>

                                    <div className="field">
                                      <label htmlFor="EventType">
                                        Event Type
                                        <Tooltip title="Classifies what this store supports (golf tournament, trade show, etc.). Used for reporting and default templates later." arrow>
                                          <InfoOutlinedIcon fontSize="small" className="field-tooltip-icon" />
                                        </Tooltip>
                                      </label>
                                      <select
                                        id="EventType"
                                        value={store.EventType || ""}
                                        onChange={(e) => handleStoreChange("EventType", e.target.value)}
                                      >
                                        <option value=""><em>None</em></option>
                                        {store.EventType && !PROMOSTORE_EVENT_TYPES.includes(store.EventType) && (
                                          <option value={store.EventType}>{store.EventType}</option>
                                        )}
                                        {PROMOSTORE_EVENT_TYPES.map((eventType) => (
                                          <option key={eventType} value={eventType}>{eventType}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </div>
                                </div>

                                <div className="details-section-group">
                                  <div className="details-section-heading">Store Address</div>
                                  <div className="order-info-fields promostore-address-settings-layout">
                                    <div className="field promostore-primary-address-field">
                                      <label htmlFor="storeAddress">Primary Store Address</label>
                                      <select
                                        id="storeAddress"
                                        value={storeAddressId || ""}
                                        onChange={(e) => {
                                          setStoreAddressId(e.target.value);
                                          setStore((prev) => ({ ...prev, isSynced: false }));
                                        }}
                                        disabled={!clientAddresses.length}
                                      >
                                        <option value="">Select store address...</option>
                                        {clientAddresses.map((addr) => (
                                          <option key={addr.id} value={addr.id}>{addr.label}</option>
                                        ))}
                                      </select>
                                      <small className="field-helper-text">Addresses are managed from the customer account.</small>
                                    </div>
                                  </div>
                                </div>

                                <div className="details-section-group promostore-checkout-group">
                                  <button
                                    type="button"
                                    className="promostore-checkout-toggle"
                                    onClick={() => setCheckoutExpanded((prev) => !prev)}
                                    aria-expanded={checkoutExpanded}
                                    aria-controls="promostore-checkout-content"
                                  >
                                    <span className="promostore-checkout-toggle-copy">
                                      <span className="promostore-checkout-toggle-title">Checkout</span>
                                      <span className="promostore-checkout-helper">
                                        The customer will need to type their name and address, or select them from a pre-defined list.
                                      </span>
                                    </span>
                                    <ExpandMoreIcon
                                      className="promostore-checkout-chevron"
                                      aria-hidden="true"
                                    />
                                  </button>

                                  {checkoutExpanded && (
                                    <div id="promostore-checkout-content" className="promostore-checkout-content">
                                      <div className="promostore-checkout-grid">
                                        {checkoutApprovalSections
                                          .filter((section) => section.group === 'contacts')
                                          .map((section) => renderCheckoutApprovalSection(section))}
                                      </div>

                                      <div className="promostore-checkout-divider" />

                                      <div className="promostore-checkout-grid">
                                        {checkoutApprovalSections
                                          .filter((section) => section.group === 'addresses')
                                          .map((section) => renderCheckoutApprovalSection(section))}
                                      </div>

                                      <div className="promostore-checkout-divider" />

                                      <div className="promostore-checkout-questions">
                                        <div className="promostore-checkout-questions-header">
                                          <div className="promostore-right-subsection-heading">Checkout Questions</div>
                                          <p className="promostore-checkout-questions-helper">
                                            Allows you to request extra information from the customer.
                                          </p>
                                        </div>
                                        <div className="promostore-checkout-question-editor">
                                          <div className="field promostore-checkout-question-input">
                                            <input
                                              id="checkout-question-text"
                                              type="text"
                                              placeholder="Enter question..."
                                              aria-label="Checkout question"
                                              maxLength={255}
                                              value={checkoutQuestionDraft.question}
                                              onChange={(event) => {
                                                setCheckoutQuestionDraft((prev) => ({ ...prev, question: event.target.value }));
                                                setCheckoutQuestionError("");
                                              }}
                                              onBlur={() => setCheckoutQuestionError(getCheckoutQuestionDraftValidation())}
                                            />
                                          </div>
                                          <div className="field promostore-checkout-question-type">
                                            <select
                                              id="checkout-question-type"
                                              aria-label="Question type"
                                              value={checkoutQuestionDraft.type}
                                              onChange={(event) => {
                                                setCheckoutQuestionDraft((prev) => ({ ...prev, type: event.target.value }));
                                                setCheckoutQuestionError("");
                                              }}
                                            >
                                              {Object.entries(PROMOSTORE_CHECKOUT_QUESTION_TYPE_LABELS).map(([value, label]) => (
                                                <option key={value} value={value}>{label}</option>
                                              ))}
                                            </select>
                                          </div>
                                          <label className="promostore-checkout-question-mandatory">
                                            <input
                                              type="checkbox"
                                              aria-label="Mandatory question"
                                              checked={checkoutQuestionDraft.mandatory}
                                              onChange={(event) => setCheckoutQuestionDraft((prev) => ({ ...prev, mandatory: event.target.checked }))}
                                            />
                                            Mandatory
                                          </label>
                                          <div className="promostore-checkout-question-editor-actions">
                                            <Button
                                              variant="contained"
                                              size="small"
                                              onClick={handleCheckoutQuestionSubmit}
                                              disabled={!!getCheckoutQuestionDraftValidation() || (!editingCheckoutQuestionId && checkoutQuestions.length >= 50)}
                                            >
                                              {editingCheckoutQuestionId ? "Save Changes" : "Add Question"}
                                            </Button>
                                            {editingCheckoutQuestionId && (
                                              <Button variant="text" size="small" onClick={resetCheckoutQuestionEditor}>Cancel</Button>
                                            )}
                                          </div>
                                          {checkoutQuestionDraft.type === "dropdown" && (
                                            <div className="field promostore-checkout-question-options">
                                              <label htmlFor="checkout-question-options">Dropdown options</label>
                                              <textarea
                                                id="checkout-question-options"
                                                rows={4}
                                                value={checkoutQuestionDraft.optionsText}
                                                onChange={(event) => {
                                                  setCheckoutQuestionDraft((prev) => ({ ...prev, optionsText: event.target.value }));
                                                  setCheckoutQuestionError("");
                                                }}
                                                onBlur={() => setCheckoutQuestionError(getCheckoutQuestionDraftValidation())}
                                              />
                                              <small className="field-helper-text">Enter one option per line.</small>
                                            </div>
                                          )}
                                          {checkoutQuestionError && (
                                            <div className="promostore-checkout-question-error" role="alert">{checkoutQuestionError}</div>
                                          )}
                                        </div>

                                        <div className="promostore-checkout-question-table-wrap">
                                          <table className="promostore-checkout-question-table">
                                            <thead>
                                              <tr>
                                                <th>Question</th>
                                                <th>Type</th>
                                                <th>Mandatory</th>
                                                <th>Order</th>
                                                <th>Actions</th>
                                              </tr>
                                            </thead>
                                            <tbody>
                                              {checkoutQuestions.length ? checkoutQuestions.map((question, index) => (
                                                <tr key={question.id}>
                                                  <td>{question.question}</td>
                                                  <td>{PROMOSTORE_CHECKOUT_QUESTION_TYPE_LABELS[question.type]}</td>
                                                  <td>{question.mandatory ? "Yes" : "No"}</td>
                                                  <td>
                                                    <div className="promostore-checkout-question-order-actions">
                                                      <button type="button" onClick={() => handleCheckoutQuestionMove(index, -1)} disabled={index === 0} aria-label={`Move ${question.question} up`}>Ã¢â€ â€˜</button>
                                                      <button type="button" onClick={() => handleCheckoutQuestionMove(index, 1)} disabled={index === checkoutQuestions.length - 1} aria-label={`Move ${question.question} down`}>Ã¢â€ â€œ</button>
                                                    </div>
                                                  </td>
                                                  <td>
                                                    <div className="promostore-checkout-question-row-actions">
                                                      <button type="button" onClick={() => handleCheckoutQuestionEdit(question)}>Edit</button>
                                                      <button type="button" className="promostore-checkout-question-delete" onClick={() => handleCheckoutQuestionDelete(question.id)}>Delete</button>
                                                    </div>
                                                  </td>
                                                </tr>
                                              )) : (
                                                <tr>
                                                  <td colSpan="5" className="promostore-checkout-question-empty">There are no questions to show here yet</td>
                                                </tr>
                                              )}
                                            </tbody>
                                          </table>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </form>
                            </div>

                            <div className="order-details-section overview-details-panel promostore-overview-side">
                              <h2 className="promostore-card-heading--blue">Store Settings</h2>
                              <form className="overview-details-form">
                                <div className="details-section-group promostore-status-summary promostore-store-settings-card">
                                  <div className="promostore-right-subsection">
                                    <div className="promostore-right-subsection-heading">Status and actions</div>

                                    <div className="promostore-status-row">
                                      <span className={`promostore-status-chip promostore-status-chip--${store.status === "closed" ? "closed" : "live"}`}>
                                        {store.status === "closed" ? "Closed" : "Live"}
                                      </span>
                                      {store.isSynced === false ? (
                                        <span className="promostore-status-chip promostore-status-chip--warning">Out of sync</span>
                                      ) : (
                                        <span className="promostore-status-chip promostore-status-chip--synced">Synced</span>
                                      )}
                                    </div>

                                    {store.isSynced === false && (
                                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                                        Your PromoStore details are out of sync with WordPress.
                                      </Alert>
                                    )}
                                  </div>

                                  <div className="promostore-right-subsection">
                                    <div className="promostore-right-subsection-heading">Commerce settings</div>
                                    <div className="promostore-commerce-layout">
                                      <div className="field">
                                        <label htmlFor="Currency">Currency</label>
                                        <select
                                          id="Currency"
                                          value={store.Currency || ""}
                                          onChange={(e) => handleStoreChange("Currency", e.target.value)}
                                        >
                                          <option value="USD">USD</option>
                                          <option value="CAD">CAD</option>
                                        </select>
                                      </div>

                                      <div className="field">
                                        <label>Shipping methods</label>
                                        <div className="promostore-checkbox-group" aria-label="Shipping Methods">
                                          {PROMOSTORE_SHIPPING_METHOD_OPTIONS.map((option) => (
                                            <label key={option.value} className="promostore-checkbox-option">
                                              <input
                                                type="checkbox"
                                                checked={shippingSettings.methods.includes(option.value)}
                                                onChange={() => toggleShippingMethod(option.value)}
                                              />
                                              <span>{option.label}</span>
                                            </label>
                                          ))}
                                        </div>
                                      </div>

                                      <div className="field promostore-payment-options-field">
                                        <label>Payment Options</label>
                                        <div className="promostore-checkbox-group" aria-label="Payment Options">
                                          <label className="promostore-checkbox-option">
                                            <input
                                              type="checkbox"
                                              checked={!!paymentSettings.requestQuote}
                                              onChange={(e) => handlePaymentSettingChange("requestQuote", e.target.checked)}
                                            />
                                            <span>Request Quote</span>
                                          </label>

                                          <label className="promostore-checkbox-option">
                                            <input
                                              type="checkbox"
                                              checked={!!paymentSettings.creditCard}
                                              onChange={(e) => handlePaymentSettingChange("creditCard", e.target.checked)}
                                            />
                                            <span>Credit Card</span>
                                          </label>
                                        </div>
                                      </div>

                                      {shippingSettings.methods.includes("flat_rate") && (
                                        <div className="field promostore-flat-rate-field">
                                          <label htmlFor="FlatRateShippingAmount">Flat rate amount</label>
                                          <input
                                            type="number"
                                            id="FlatRateShippingAmount"
                                            min="0"
                                            step="0.01"
                                            value={shippingSettings.flatRateAmount}
                                            onChange={(e) => handleShippingSettingChange("flatRateAmount", e.target.value)}
                                            placeholder="0.00"
                                          />
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>

                                <section className="promostore-quote-po-section">
                                  <div
                                    className="promostore-quote-po-header"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={quotePurchaseOrderExpanded}
                                    aria-controls="promostore-quote-po-content"
                                    onClick={() => setQuotePurchaseOrderExpanded((expanded) => !expanded)}
                                    onKeyDown={(event) => handlePromoStoreHeaderKeyDown(
                                      event,
                                      () => setQuotePurchaseOrderExpanded((expanded) => !expanded)
                                    )}
                                  >
                                    <div className="promostore-quote-po-header-title">
                                      <span className="promostore-quote-po-title">QUOTE / P.O. SETTINGS</span>
                                      <PromoStoreInlineHelp
                                        label="Quote and P.O. settings help"
                                        stopPropagation
                                        title={(
                                          <>
                                            <div>Controls Quote Request checkout and Purchase Order options.</div>
                                            <div>When enabled, customers can request a quote instead of paying during checkout.</div>
                                          </>
                                        )}
                                      />
                                    </div>
                                    <div className="promostore-quote-po-master">
                                      <span className="promostore-quote-po-master-label">Quote Gateway</span>
                                      <Switch
                                        size="small"
                                        checked={quotePurchaseOrderSettings.quoteGateway.enabled}
                                        onChange={(event) => handleQuotePurchaseOrderSettingChange("quoteGateway", "enabled", event.target.checked)}
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}
                                        inputProps={{ "aria-label": "Enable Quote Gateway" }}
                                        className="promostore-quote-po-master-switch"
                                      />
                                      <span
                                        className="promostore-quote-po-disclosure"
                                        aria-hidden="true"
                                      >
                                        <ExpandMoreIcon
                                          aria-hidden="true"
                                          className={`promostore-quote-po-chevron${quotePurchaseOrderExpanded ? " is-expanded" : ""}`}
                                        />
                                      </span>
                                    </div>
                                  </div>

                                  {quotePurchaseOrderExpanded && quotePurchaseOrderSettings.quoteGateway.enabled && (
                                    <div id="promostore-quote-po-content" className="promostore-quote-po-grid">
                                      <div className={`promostore-quote-po-card${quotePurchaseOrderSettings.quoteGateway.enabled ? "" : " is-disabled"}`}>
                                        <h3>Quote / P.O. Gateway</h3>
                                        <div className="field">
                                          <div className="promostore-quote-po-field-label">
                                            <label htmlFor="QuoteGatewayTitle">Gateway Title</label>
                                            <PromoStoreInlineHelp label="Gateway Title help" title="The payment option name customers see during checkout." />
                                          </div>
                                          <input id="QuoteGatewayTitle" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.quoteGateway.title} onChange={(event) => handleQuotePurchaseOrderSettingChange("quoteGateway", "title", event.target.value)} />
                                        </div>
                                        <div className="field">
                                          <div className="promostore-quote-po-field-label">
                                            <label htmlFor="QuoteGatewayButtonText">Button Text</label>
                                            <PromoStoreInlineHelp label="Button Text help" title="The label shown on the checkout submission button when Request Quote is selected." />
                                          </div>
                                          <input id="QuoteGatewayButtonText" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.quoteGateway.buttonText} onChange={(event) => handleQuotePurchaseOrderSettingChange("quoteGateway", "buttonText", event.target.value)} />
                                        </div>
                                        <div className="field">
                                          <div className="promostore-quote-po-field-label">
                                            <label htmlFor="QuoteGatewayDescription">Gateway Description</label>
                                            <PromoStoreInlineHelp label="Gateway Description help" title="The message shown with the Request Quote payment option during checkout." />
                                          </div>
                                          <textarea id="QuoteGatewayDescription" rows="3" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.quoteGateway.description} onChange={(event) => handleQuotePurchaseOrderSettingChange("quoteGateway", "description", event.target.value)} />
                                        </div>
                                      </div>

                                      <div className={`promostore-quote-po-card${quotePurchaseOrderSettings.quoteGateway.enabled ? "" : " is-disabled"}`}>
                                        <h3>P.O. / Checkout Fields</h3>
                                        <div className="promostore-quote-po-toggle-row">
                                          <label className="promostore-checkbox-option promostore-quote-po-toggle">
                                            <input
                                              type="checkbox"
                                              disabled={!quotePurchaseOrderSettings.quoteGateway.enabled}
                                              checked={quotePurchaseOrderSettings.purchaseOrder.enabled}
                                              onChange={(event) => handleQuotePurchaseOrderSettingChange("purchaseOrder", "enabled", event.target.checked)}
                                            />
                                            <span>Enable P.O. Mode</span>
                                          </label>
                                          <PromoStoreInlineHelp label="P.O. Mode help" title="Asks whether the customer has a P.O. number. If Yes, the P.O. number is required and Request Quote becomes the only payment option. If No, normal payment options remain available." />
                                        </div>
                                        <div className="field">
                                          <div className="promostore-quote-po-field-label">
                                            <label htmlFor="PurchaseOrderQuestionLabel">P.O. Question Label</label>
                                            <PromoStoreInlineHelp label="P.O. Question Label help" title="The question shown to customers asking whether they have a purchase order number." />
                                          </div>
                                          <input id="PurchaseOrderQuestionLabel" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.purchaseOrder.questionLabel} onChange={(event) => handleQuotePurchaseOrderSettingChange("purchaseOrder", "questionLabel", event.target.value)} />
                                        </div>
                                        <div className="field">
                                          <div className="promostore-quote-po-field-label">
                                            <label htmlFor="PurchaseOrderNumberLabel">P.O. Number Label</label>
                                            <PromoStoreInlineHelp label="P.O. Number Label help" title="The label shown above the purchase order number field." />
                                          </div>
                                          <input id="PurchaseOrderNumberLabel" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.purchaseOrder.numberLabel} onChange={(event) => handleQuotePurchaseOrderSettingChange("purchaseOrder", "numberLabel", event.target.value)} />
                                        </div>
                                        <div className="promostore-quote-po-option-heading">
                                          <span>P.O. Option Labels</span>
                                          <PromoStoreInlineHelp label="P.O. Option Labels help" title="Customizes the Yes and No choices shown for the purchase order question." />
                                        </div>
                                        <div className="promostore-quote-po-option-labels">
                                          <div className="field">
                                            <label htmlFor="PurchaseOrderYesLabel">Yes Label</label>
                                            <input id="PurchaseOrderYesLabel" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.purchaseOrder.yesLabel} onChange={(event) => handleQuotePurchaseOrderSettingChange("purchaseOrder", "yesLabel", event.target.value)} />
                                          </div>
                                          <div className="field">
                                            <label htmlFor="PurchaseOrderNoLabel">No Label</label>
                                            <input id="PurchaseOrderNoLabel" disabled={!quotePurchaseOrderSettings.quoteGateway.enabled} value={quotePurchaseOrderSettings.purchaseOrder.noLabel} onChange={(event) => handleQuotePurchaseOrderSettingChange("purchaseOrder", "noLabel", event.target.value)} />
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </section>

                                <div className="promostore-tax-configuration-card">
                                  <div
                                    className="promostore-tax-header"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={taxConfigurationExpanded}
                                    aria-controls="promostore-tax-content"
                                    onClick={() => setTaxConfigurationExpanded((expanded) => !expanded)}
                                    onKeyDown={(event) => handlePromoStoreHeaderKeyDown(
                                      event,
                                      () => setTaxConfigurationExpanded((expanded) => !expanded)
                                    )}
                                  >
                                    <div className="promostore-tax-header-title">
                                      <span>TAX CONFIGURATION</span>
                                      <PromoStoreInlineHelp label="Tax configuration help" title="Tax CSV files are not applied by Initiate Sync. Apply them here after validation." stopPropagation />
                                      <span className={`promostore-tax-dashboard-badge promostore-tax-dashboard-badge--${taxOverallStatus.toLowerCase().replace(/\s+/g, "-")}`}>
                                        {taxOverallStatus}
                                      </span>
                                    </div>
                                    <div className="promostore-tax-header-actions">
                                      <span>Calculate taxes on checkout</span>
                                      <Switch
                                        size="small"
                                        checked={store.CalculateTaxes || false}
                                        onChange={(e) => handleStoreChange("CalculateTaxes", e.target.checked)}
                                        onClick={(event) => event.stopPropagation()}
                                        onKeyDown={(event) => event.stopPropagation()}
                                        inputProps={{ "aria-label": "Calculate taxes on checkout" }}
                                        className="promostore-tax-master-switch"
                                      />
                                      <span
                                        className="promostore-tax-disclosure"
                                        aria-hidden="true"
                                      >
                                        <ExpandMoreIcon className={`promostore-tax-chevron${taxConfigurationExpanded ? " is-expanded" : ""}`} aria-hidden="true" />
                                      </span>
                                    </div>
                                  </div>

                                  {taxConfigurationExpanded && (
                                  <div
                                    id="promostore-tax-content"
                                    className="promostore-tax-region-container"
                                    data-tax-latest-action={latestTaxHistoryItem ? `${getTaxHistoryActionLabel(latestTaxHistoryItem.action)} - ${getTaxHistoryStatusLabel(latestTaxHistoryItem.status)}` : taxHistoryError || undefined}
                                  >
                                    <div className="field promostore-tax-region-field">
                                      <label htmlFor="TaxRegion">Tax Region</label>
                                      <select
                                        id="TaxRegion"
                                        value={taxRegionDraft}
                                        onChange={(e) => handleTaxRegionChange(e.target.value)}
                                        disabled={taxCsvWorking.applying || taxRestoreWorking}
                                      >
                                        <option value="canada">Canada</option>
                                        <option value="united_states">United States</option>
                                        <option value="both">Both</option>
                                      </select>
                                    </div>
                                    <div className="promostore-tax-accordion-list">
                                      {taxDashboardRegions.map((region) => {
                                        const file = taxCsvFiles[region.region];
                                        const validation = taxCsvValidation[region.region];
                                        const controlsDisabled = taxCsvWorking.applying || taxRestoreWorking;
                                        const isRequired = requiredTaxCsvRegions.includes(region.region);
                                        return (
                                          <Accordion
                                            key={`tax-dashboard-${region.key}`}
                                            expanded={expandedTaxCountry === region.region}
                                            onChange={(_event, expanded) => setExpandedTaxCountry(expanded ? region.region : false)}
                                            className="promostore-tax-country-accordion"
                                            disableGutters
                                          >
                                            <AccordionSummary
                                              expandIcon={<ExpandMoreIcon aria-hidden="true" />}
                                              aria-controls={`tax-country-${region.region}-content`}
                                              id={`tax-country-${region.region}-header`}
                                              className="promostore-tax-country-summary"
                                            >
                                              <div className="promostore-tax-status-card-title"><span aria-hidden="true">{region.region === "canada" ? "CA" : "US"}</span>{region.label}</div>
                                              <span className={`promostore-tax-region-status-badge promostore-tax-region-status-badge--${region.status.toLowerCase().replace(/\s+/g, "-")}`}>
                                                {region.status}
                                              </span>
                                            </AccordionSummary>
                                            <AccordionDetails id={`tax-country-${region.region}-content`} className="promostore-tax-country-details">
                                            <div>Rates: {region.rowCount !== undefined ? region.rowCount : "Rate count unavailable"}</div>
                                            {region.appliedAt ? <div>Last applied: {new Date(region.appliedAt).toLocaleString()}</div> : <div>Not applied yet</div>}
                                            {region.appliedByName && <div>Applied by: {region.appliedByName}</div>}
                                            {region.filename && <div>File: {region.filename}</div>}
                                            {(file || validation || taxCsvWorking[region.region]) && (
                                              <div className={`promostore-tax-upload-panel promostore-tax-upload-panel--compact ${isRequired ? "is-required" : ""}`}>
                                                {file && (
                                                  <>
                                                    <div className="promostore-tax-upload-empty">{file.name}</div>
                                                    <div className="promostore-tax-file-meta">
                                                      <span>{formatTaxCsvSize(file)}</span>
                                                      <span>Selected region: {region.label}</span>
                                                    </div>
                                                  </>
                                                )}
                                                <div className="promostore-tax-validation-state" aria-live="polite">
                                                  {taxCsvWorking[region.region] ? (
                                                    <><CircularProgress size={14} /> Validating...</>
                                                  ) : validation?.valid && validation?.alreadyActive ? (
                                                    <>Already active - {validation.rowCount} tax rates</>
                                                  ) : validation?.valid ? (
                                                    <>Valid and ready to apply - {validation.rowCount} tax rates</>
                                                  ) : validation ? (
                                                    <>Invalid CSV</>
                                                  ) : null}
                                                </div>
                                                {validation?.valid && validation?.detectedStates?.length > 0 && (
                                                  <div className="promostore-tax-regions-summary">
                                                    <button
                                                      type="button"
                                                      className="promostore-tax-toggle-button"
                                                      aria-expanded={!!taxExpandedRegions[region.region]}
                                                      aria-controls={`tax-regions-${region.region}`}
                                                      onClick={() => setTaxExpandedRegions((prev) => ({ ...prev, [region.region]: !prev[region.region] }))}
                                                    >
                                                      {taxExpandedRegions[region.region] ? "Hide regions" : "View regions"}
                                                    </button>
                                                    {taxExpandedRegions[region.region] && (
                                                      <div id={`tax-regions-${region.region}`} className="promostore-tax-region-list">
                                                        {validation.detectedStates.join(", ")}
                                                      </div>
                                                    )}
                                                  </div>
                                                )}
                                                {validation?.errors?.length > 0 && (
                                                  <div className="promostore-tax-error-summary">
                                                    <div>{validation.errors.length} validation errors found.</div>
                                                    <ul className="promostore-tax-errors" id={`tax-errors-${region.region}`}>
                                                      {(taxExpandedErrors[region.region] ? validation.errors : validation.errors.slice(0, 3)).map((error, idx) => (
                                                        <li key={`${region.region}-error-${idx}`}>{error.row ? `Row ${error.row}: ` : ""}{error.message}</li>
                                                      ))}
                                                    </ul>
                                                    {validation.errors.length > 3 && (
                                                      <button
                                                        type="button"
                                                        className="promostore-tax-toggle-button"
                                                        aria-expanded={!!taxExpandedErrors[region.region]}
                                                        aria-controls={`tax-errors-${region.region}`}
                                                        onClick={() => setTaxExpandedErrors((prev) => ({ ...prev, [region.region]: !prev[region.region] }))}
                                                      >
                                                        {taxExpandedErrors[region.region] ? "Show fewer errors" : "Show all errors"}
                                                      </button>
                                                    )}
                                                  </div>
                                                )}
                                                {file && (
                                                  <div className="promostore-tax-upload-actions">
                                                    <button type="button" className="promostore-tax-remove-button" onClick={() => handleTaxCsvRemove(region.region)} disabled={controlsDisabled}>
                                                      Remove
                                                    </button>
                                                  </div>
                                                )}
                                              </div>
                                            )}
                                            <label className="promostore-tax-upload-button promostore-tax-upload-button--card-action">
                                              {file ? "Replace CSV" : "Upload CSV"}
                                              <input
                                                type="file"
                                                accept=".csv,text/csv"
                                                disabled={controlsDisabled}
                                                onChange={(e) => handleTaxCsvSelect(region.region, e.target.files?.[0])}
                                              />
                                            </label>
                                            </AccordionDetails>
                                          </Accordion>
                                        );
                                      })}
                                    </div>

                                  {canRestoreTaxRates && (
                                    <button
                                      type="button"
                                      className="promostore-tax-restore-button"
                                      disabled={taxRestoreWorking || taxCsvWorking.applying}
                                      onClick={() => setTaxRestoreConfirmOpen(true)}
                                    >
                                      {taxRestoreButtonLabel}
                                    </button>
                                  )}

                                  {taxCsvStatus && (
                                    <div className={`promostore-tax-status ${taxCsvStatus?.type ? `promostore-tax-status--${taxCsvStatus.type}` : ""}`}>
                                      {taxCsvStatus?.type === "info" && (
                                      <div className="promostore-tax-info-details">{taxCsvStatus.message}</div>
                                    )}
                                    {taxCsvStatus?.type === "success" && (
                                      <div className="promostore-tax-success-details">
                                        {taxCsvStatus.noChanges ? (
                                          <>
                                            <strong>No tax changes were applied.</strong>
                                            <div>Reason: {taxCsvStatus.message || "The selected file is already active."}</div>
                                          </>
                                        ) : (
                                          <>
                                            <strong>{taxCsvStatus.action === "restore" ? taxCsvStatus.message : `${getTaxRegionLabel(taxCsvStatus.region)} tax rates applied successfully.`}</strong>
                                            {getTaxImportedCount(taxCsvStatus.results) > 0 && (
                                              <div>{getTaxImportedCount(taxCsvStatus.results)} tax rates imported</div>
                                            )}
                                            {taxCsvStatus.appliedAt && <div>Applied: {new Date(taxCsvStatus.appliedAt).toLocaleString()}</div>}
                                          </>
                                        )}
                                        {!taxCsvStatus.noChanges && taxCsvStatus.unchangedRegions?.length > 0 && (
                                          <div>{taxCsvStatus.unchangedRegions.map(getTaxRegionLabel).join(" and ")} already active and not changed.</div>
                                        )}
                                        {taxCsvStatus.warning && taxCsvStatus.message && <div>{taxCsvStatus.message}</div>}
                                        {!taxCsvStatus.noChanges && taxCsvStatus.results && (
                                          <button
                                            type="button"
                                            className="promostore-tax-toggle-button"
                                            aria-expanded={taxSuccessDetailsExpanded}
                                            aria-controls="tax-apply-result-details"
                                            onClick={() => setTaxSuccessDetailsExpanded((prev) => !prev)}
                                          >
                                            {taxSuccessDetailsExpanded ? "Hide details" : "View details"}
                                          </button>
                                        )}
                                        {taxSuccessDetailsExpanded && (
                                          <div id="tax-apply-result-details" className="promostore-tax-result-details">
                                            {taxCsvStatus.results?.canada && (
                                              <div className="promostore-tax-result-group">
                                                {taxCsvStatus.results.unitedStates && <div className="promostore-tax-result-region">Canada</div>}
                                                {renderTaxResultCounts(taxCsvStatus.results.canada)}
                                              </div>
                                            )}
                                            {taxCsvStatus.results?.unitedStates && (
                                              <div className="promostore-tax-result-group">
                                                {taxCsvStatus.results.canada && <div className="promostore-tax-result-region">United States</div>}
                                                {renderTaxResultCounts(taxCsvStatus.results.unitedStates)}
                                              </div>
                                            )}
                                            {taxCsvStatus.appliedBy && <div>Applied by: {taxCsvStatus.appliedBy}</div>}
                                            {taxCsvStatus.restoredAt && <div>Restored: {new Date(taxCsvStatus.restoredAt).toLocaleString()}</div>}
                                            {taxCsvStatus.restoredBy && <div>Restored by: {taxCsvStatus.restoredBy}</div>}
                                          </div>
                                        )}
                                      </div>
                                    )}
                                      {taxCsvStatus?.type === "error" && (
                                        <div className="promostore-tax-error-details">
                                        <strong>{taxCsvStatus.action === "download" ? "Unable to download current tax rates." : taxCsvStatus.action === "preview" ? "Unable to preview tax changes." : taxCsvStatus.action === "restore" ? "Unable to restore previous tax rates." : `Unable to apply ${getTaxRegionLabel(taxCsvStatus.region || taxRegionDraft)} tax rates.`}</strong>
                                        {taxCsvStatus.message && <div><span>Reason:</span> {taxCsvStatus.message}</div>}
                                        {requiredTaxCsvRegions.map((region) => {
                                          const validation = taxCsvValidation[region];
                                          if (!validation || validation.valid || !validation.errors?.length) return null;
                                          return <div key={`apply-error-${region}`}>{getTaxRegionLabel(region)} validation errors: {validation.errors.length}</div>;
                                        })}
                                        {taxCsvStatus.rollbackRestored && <div>Previous WooCommerce tax rates were restored. No partial changes remain.</div>}
                                      </div>
                                      )}
                                    </div>
                                  )}
                                  <div className="promostore-tax-apply-row" data-tax-readiness={taxReadinessMessage || undefined}>
                                    <Tooltip title="Upload and validate a tax CSV before applying." arrow>
                                      <span className="promostore-tax-apply-wrapper">
                                      <button
                                        type="button"
                                        className="promostore-tax-apply-button"
                                        disabled={!canApplyTaxCsv}
                                        onClick={() => setTaxApplyConfirmOpen(true)}
                                      >
                                        {taxApplyButtonLabel}
                                      </button>
                                      </span>
                                    </Tooltip>
                                  </div>
                                  </div>
                                  )}
                                </div>

                                <div className="details-section-group promostore-email-settings">
                                  <div className="details-section-heading promostore-card-heading--purple">
                                    <span>Order notification emails</span>
                                    <small>Enter multiple recipients separated by commas.</small>
                                  </div>
                                  <div className="promostore-email-list">
                                    {emailNotificationRows.map(({ key, label }) => {
                                      const Icon = key === "newOrder" ? ShoppingCartOutlinedIcon : key === "failedOrder" ? WarningAmberOutlinedIcon : BlockOutlinedIcon;
                                      return (
                                        <div className={`promostore-email-row promostore-email-row--${key}`} key={key}>
                                          <div className="promostore-email-row-title">
                                            <Icon fontSize="small" aria-hidden="true" />
                                            <span>{label}</span>
                                          </div>
                                          <div className="field">
                                            <input
                                              type="text"
                                              id={`${key}-recipients`}
                                              aria-label={`${label} recipient email addresses`}
                                              value={emailNotifications[key]?.recipients || ""}
                                              onChange={(e) => handleEmailNotificationChange(key, "recipients", e.target.value)}
                                              placeholder={store.Email || "Comma-separated email addresses"}
                                            />
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>

                                <Box className="save-changes-section promostore-overview-actions">
                                  <Button onClick={handleSaveOverview}>Save Overview</Button>
                                  <Button onClick={fetchPromostore} variant="outlined">Refresh</Button>
                                  <Button onClick={handleInitiateSync} variant="outlined" disabled={syncing}>
                                    {syncing ? "Syncing..." : "Initiate Sync"}
                                  </Button>
                                </Box>
                              </form>
                            </div>
                          </div>
                        )}
                      </div>

                      {activeTab === "Orders" && (
                        <div className="orders-section">
                          <InstanceWebStoreOrders
                            orders={store.Orders || store.orders || []}
                            instanceUniqueId={instanceUniqueId}
                            storeId={store.id}
                            storeName={store.ShopName || store.shopName || store.title || store.domain || "PromoStore"}
                            isActive={activeTab === "Orders"}
                          />
                        </div>
                      )}

                      {activeTab === "Reports" && (
                        <div className="orders-section">
                          <InstancePromoStoreReports
                            store={store}
                            promoStoreId={id}
                            instanceUniqueId={instanceUniqueId}
                          />
                        </div>
                      )}

                      {activeTab === "Users" && (
                        <div className="orders-section">
                          <InstancePromoStoreUsers
                            instanceUniqueId={instanceUniqueId}
                            storeId={store.id}
                            isActive={activeTab === "Users"}
                          />
                        </div>
                      )}
                    </div>
                  </div>


                  <PromoStoreCategoriesDialog
                    open={categoriesDialogOpen}
                    categories={promoStoreCategories}
                    products={order?.Products || []}
                    saving={categorySaving}
                    onClose={() => setCategoriesDialogOpen(false)}
                    onCreate={handleCreatePromoStoreCategory}
                    onRename={handleRenamePromoStoreCategory}
                    onDelete={handleDeletePromoStoreCategory}
                    onMove={handleMovePromoStoreCategory}
                  />

                  <Dialog open={taxApplyConfirmOpen} onClose={() => !taxCsvWorking.applying && setTaxApplyConfirmOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Apply WooCommerce tax rates?</DialogTitle>
                    <DialogContent>
                      <Typography sx={{ mb: 1.5 }}>
                        PromoStore: <strong>{store.ShopName || store.title || "this PromoStore"}</strong>
                      </Typography>
                      <Typography sx={{ mb: 1.5 }}>
                        Selected region: <strong>{taxRegionLabel}</strong>
                      </Typography>
                      <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 2.5 }}>
                        {requiredTaxCsvRegions.includes("canada") && <li>Canada validated rows: {taxCsvValidation.canada?.rowCount || 0}</li>}
                        {requiredTaxCsvRegions.includes("united_states") && <li>United States validated rows: {taxCsvValidation.united_states?.rowCount || 0}</li>}
                      </Box>
                      <Typography sx={{ mb: 1.5 }}>
                        You are about to replace the active WooCommerce tax rates for the selected region on this PromoStore. This action is separate from normal PromoStore sync.
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Products, pricing, orders, customers, shipping settings, payment settings, and unrelated tax regions will not be changed. Validation will run again on the server before anything is sent to WordPress.
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button variant="outlined" onClick={() => setTaxApplyConfirmOpen(false)} disabled={taxCsvWorking.applying}>Cancel</Button>
                      <Button onClick={handleApplyTaxCsv} disabled={!canApplyTaxCsv || taxCsvWorking.applying}>
                        {taxApplyButtonLabel}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  <Dialog open={taxRestoreConfirmOpen} onClose={() => !taxRestoreWorking && setTaxRestoreConfirmOpen(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Restore Previous {taxRegionLabel} Tax Rates?</DialogTitle>
                    <DialogContent>
                      <Typography sx={{ mb: 1.5 }}>
                        This will replace the currently active {taxRegionLabel} tax rates with the previous saved configuration.
                      </Typography>
                      <Box component="ul" sx={{ mt: 0, mb: 1.5, pl: 2.5 }}>
                        {requiredTaxCsvRegions.map((region) => {
                          const key = getTaxMetadataRegionKey(region);
                          const snapshot = taxRestoreSnapshots?.[key];
                          const active = taxLastApplied?.[key];
                          return (
                            <li key={`restore-confirm-${region}`}>
                              <strong>{getTaxRegionLabel(region)}</strong>: current {active?.appliedAt ? new Date(active.appliedAt).toLocaleString() : "active version"}; previous captured {snapshot?.capturedAt ? new Date(snapshot.capturedAt).toLocaleString() : "unknown"} ({snapshot?.rowCount ?? 0} rows)
                            </li>
                          );
                        })}
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Products, pricing, customers, orders, shipping, payments, and unrelated tax regions will not be changed.
                      </Typography>
                    </DialogContent>
                    <DialogActions>
                      <Button variant="outlined" onClick={() => setTaxRestoreConfirmOpen(false)} disabled={taxRestoreWorking}>Cancel</Button>
                      <Button color="warning" onClick={handleRestoreTaxRates} disabled={!canRestoreTaxRates || taxRestoreWorking}>
                        {taxRestoreButtonLabel}
                      </Button>
                    </DialogActions>
                  </Dialog>

                  {/* Files Tab */}
                  {activeTab === "Files" && (
                    <div className="order-details-section">
                      <OrderFiles
                        store={store}
                        onSelectLogo={(file, updatedStore) => {
                          if (updatedStore) {
                            const normalizedUpdatedStore = normalizePromoStoreRecord(updatedStore);
                            setStore(normalizedUpdatedStore);
                            latestStoreRef.current = normalizedUpdatedStore;
                            return;
                          }
                          setStore((prev) => ({
                            ...prev,
                            ShopLogoUrl: file.filePath,
                            isSynced: false,
                          }));
                        }}
                        onSelectBanner={(file, updatedStore) => {
                          if (updatedStore) {
                            const normalizedUpdatedStore = normalizePromoStoreRecord(updatedStore);
                            setStore(normalizedUpdatedStore);
                            latestStoreRef.current = normalizedUpdatedStore;
                            return;
                          }
                          setStore((prev) => ({
                            ...prev,
                            HomepageBannerUrl: file.filePath,
                            isSynced: false,
                          }));
                        }}
                      />
                    </div>
                  )}

                  {activeTab === "Products" && (
                    <>
                      <PresentationTabs
                        fetchOrder={fetchPromostore}
                        order={order}
                        selectedProducts={selectedProducts}
                        handleShowProductsPopup={handleShowProductsPopup}
                        isPromoStore={true}
                        onSavePromoStoreProduct={updateItemsToOrder}
                        secondaryActionLabel="Categories"
                        onSecondaryAction={() => setCategoriesDialogOpen(true)}
                        promoStoreCategories={promoStoreCategories}
                      />

                      {showProductManagerPopup && (
                        <InstanceProductManager
                          popupMode={popupInitialView}
                          isPresentation={true}
                          closeProductListPopup={closeProductListPopup}
                          productId={currentProduct || ""}
                          order={order}
                          updateItemsToOrder={updateItemsToOrder}
                          instanceUniqueId={instanceUniqueId}
                          handleSaveProduct={handleSaveProduct}
                        />
                      )}
                    </>
                  )}
                </>
              )
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstancePromoStoreDetails;
